/**
 * AuditTrailPageV2 — proper v2 redesign of the audit trail surface (G121).
 *
 * Canonical inventory pattern. Surfaces the universal audit trail
 * (policy decisions, agent executions, governance changes) with
 * verifiable hash chain integrity.
 *
 * Reuses: api.getPolicyAuditTrail / verifyAuditIntegrity /
 * getWorkflowAuditTrail.
 *
 * v1 stays as fallback via /audit?legacy=1.
 */
import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useToast } from '../contexts/ToastContext';
import { useAuth } from '../contexts/AuthContext';
import * as api from '../services/api';
import {
  RefreshCw, ShieldCheck, Activity, AlertTriangle, CheckCircle, XCircle,
  Eye, Download, Lock, Search, X,
} from 'lucide-react';
import {
  Button, Pill, Card, Modal, Slideout, Table, FacetRail, EmptyState,
  MetricCard, FiveLayerTrace,
} from '../components/v2';
import { Pagination, usePaginationState } from '../components/common/Pagination';
import {
  buildVerifyArgs,
  chainLabelOf,
  verdictPresentation,
  adaptBreak,
  hasStructuredBreaks,
} from '../utils/audit/verifyScope';

import WorkflowAuditPage from './WorkflowAuditPage';
/* G121 — note: WorkflowAuditPage is the v1 audit surface; this v2 redesign
 * replaces the chrome and adds FacetRail + Slideout inventory pattern. */

/* G222 — Event type and decision keys must match backend PolicyAuditEvent.
 * Backend constants live in PolicyAuditEvent.EventType (backend model).
 * The list below mirrors that catalog and groups types by family so
 * operators can find the lifecycle event they care about without scanning.
 *
 *   decision:  ALLOWED, BLOCKED, WARN, REDACTED, ESCALATED, LOGGED, FAILED
 *
 * Previous V2 keys (POLICY_DECISION / allow / block) never matched, so every
 * facet count was 0 and the KPI tiles read 0 allows / 0 blocks even with
 * thousands of real events.
 *
 * PROVE7-JOURNEY sweep 2026-05-25 — Surfaced the gap: only 12 of ~32 emitted
 * event types had filter chips. Added cognitive-runtime, framework-binding,
 * WASM, SPIFFE, Intent-template/binding-lifecycle, and runtime-source-flip
 * types so operators can filter the chain by agent-lifecycle activity. */
const EVENT_TYPE_OPTIONS = [
  // ── Policy + data plane (12) ─────────────────────────────────
  { key: 'POLICY_RULE_EVALUATION', label: 'Policy rule evaluation', group: 'Policy & data' },
  { key: 'SEMANTIC_EVALUATION',    label: 'Semantic evaluation',    group: 'Policy & data' },
  { key: 'DATA_CLASSIFICATION',    label: 'Data classification',    group: 'Policy & data' },
  { key: 'DATA_REDACTION',         label: 'Data redaction',         group: 'Policy & data' },
  { key: 'AGENT_ENFORCEMENT',      label: 'Agent enforcement',      group: 'Policy & data' },
  { key: 'INFERENCE_CALL',         label: 'Inference call',         group: 'Policy & data' },
  { key: 'MCP_TOOL_INVOCATION',    label: 'MCP tool invocation',    group: 'Policy & data' },
  // A governed command refused for its own shape — an argument the action's manifest never
  // declared, or a payload past the customer's configured ceiling. No rule was consulted, because
  // the request did not describe an action the platform offers.
  { key: 'COMMAND_VOCABULARY_REFUSED', label: 'Command refused', group: 'Policy & data' },
  // Both were emitted by the platform and silently rejected by the event_type CHECK until V411, so
  // neither had a filter because neither had ever produced a row.
  { key: 'PII_REVEAL',             label: 'PII reveal',             group: 'Policy & data' },
  { key: 'CONFORMANCE_SCORED',     label: 'Conformance scored',     group: 'Policy & data' },
  { key: 'ANOMALY_DETECTED',       label: 'Anomaly detected',       group: 'Policy & data' },
  { key: 'EMERGENCY_OVERRIDE',     label: 'Emergency override',     group: 'Policy & data' },
  { key: 'TOKEN_CREATED',          label: 'Token created',          group: 'Policy & data' },
  { key: 'TOKEN_RESOLVED',         label: 'Token resolved',         group: 'Policy & data' },
  { key: 'TOKEN_EXPIRED',          label: 'Token expired',          group: 'Policy & data' },
  // ── Cognitive runtime (9) — G262 / AGENT_EXECUTION_MODEL §6 ──
  { key: 'EXECUTION_STARTED',      label: 'Execution started',      group: 'Cognitive runtime' },
  { key: 'COGNITIVE_ITERATION',    label: 'Cognitive iteration',    group: 'Cognitive runtime' },
  { key: 'TOOL_CALL_DECIDED',      label: 'Tool-call decided',      group: 'Cognitive runtime' },
  { key: 'TOOL_CALL_PRE_POLICY',   label: 'Tool-call pre-policy',   group: 'Cognitive runtime' },
  { key: 'TOOL_CALL_EXECUTED',     label: 'Tool-call executed',     group: 'Cognitive runtime' },
  { key: 'TOOL_CALL_POST_POLICY',  label: 'Tool-call post-policy',  group: 'Cognitive runtime' },
  { key: 'TOOL_CALL_RETURNED',     label: 'Tool-call returned',     group: 'Cognitive runtime' },
  { key: 'COGNITIVE_ESCALATION',   label: 'Cognitive escalation',   group: 'Cognitive runtime' },
  { key: 'EXECUTION_COMPLETED',    label: 'Execution completed',    group: 'Cognitive runtime' },
  // ── Identity & sandbox (4) ───────────────────────────────────
  { key: 'SPIFFE_SVID_ISSUED',     label: 'SPIFFE SVID issued',     group: 'Identity & sandbox' },
  { key: 'WASM_SKILL_EXECUTED',    label: 'WASM skill executed',    group: 'Identity & sandbox' },
  { key: 'WASM_SKILL_FAILED',      label: 'WASM skill failed',      group: 'Identity & sandbox' },
  { key: 'WASM_HOST_IMPORT_INVOKED', label: 'WASM host import invoked', group: 'Identity & sandbox' },
  // ── Canvas reconcile (3) ─────────────────────────────────────
  { key: 'CANVAS_RECONCILE_ADDED',   label: 'Canvas reconcile · added',   group: 'Canvas reconcile' },
  { key: 'CANVAS_RECONCILE_REMOVED', label: 'Canvas reconcile · removed', group: 'Canvas reconcile' },
  { key: 'CANVAS_RECONCILE_MUTATED', label: 'Canvas reconcile · mutated', group: 'Canvas reconcile' },
  // ── Framework binding (1) ────────────────────────────────────
  { key: 'FRAMEWORK_BINDING_TRANSITION', label: 'Framework-binding transition', group: 'Framework binding' },
  // ── Intent authorization (1) ─────────────────────────────────
  { key: 'INTENT_AUTHORIZATION',   label: 'Intent authorization',   group: 'Intent authorization' },
  // ── Intent template lifecycle (8) — V212 Phase 1B ────────────
  { key: 'INTENT_TEMPLATE_CREATED',                    label: 'Intent template created',     group: 'Intent template lifecycle' },
  { key: 'INTENT_TEMPLATE_VERSION_EDITED',             label: 'Template version edited',     group: 'Intent template lifecycle' },
  { key: 'INTENT_TEMPLATE_VERSION_SUBMITTED',          label: 'Template version submitted',  group: 'Intent template lifecycle' },
  { key: 'INTENT_TEMPLATE_VERSION_APPROVED',           label: 'Template version approved',   group: 'Intent template lifecycle' },
  { key: 'INTENT_TEMPLATE_VERSION_REJECTED',           label: 'Template version rejected',   group: 'Intent template lifecycle' },
  { key: 'INTENT_TEMPLATE_VERSION_DEPRECATED',         label: 'Template version deprecated', group: 'Intent template lifecycle' },
  { key: 'INTENT_TEMPLATE_VERSION_FLAGGED_CRITICAL',   label: 'Template flagged critical',   group: 'Intent template lifecycle' },
  { key: 'INTENT_TEMPLATE_ARCHIVED',                   label: 'Intent template archived',    group: 'Intent template lifecycle' },
  { key: 'INTENT_TEMPLATE_VERSION_BROKEN_REFS_DETECTED', label: 'Template broken-refs detected', group: 'Intent template lifecycle' },
  { key: 'INTENT_TEMPLATE_PURGED',                     label: 'Intent template purged',      group: 'Intent template lifecycle' },
  // ── Intent binding lifecycle (8) — V212/V215/V217/V218 ───────
  { key: 'INTENT_BINDING_ATTACHED',           label: 'Binding attached',            group: 'Intent binding lifecycle' },
  { key: 'INTENT_BINDING_DETACHED',           label: 'Binding detached',            group: 'Intent binding lifecycle' },
  { key: 'INTENT_BINDING_NARROWED',           label: 'Binding narrowed',            group: 'Intent binding lifecycle' },
  { key: 'INTENT_BINDING_REBOUND',            label: 'Binding rebound',             group: 'Intent binding lifecycle' },
  { key: 'INTENT_BINDING_DRIFT_DETECTED',     label: 'Binding drift detected',      group: 'Intent binding lifecycle' },
  { key: 'INTENT_BINDING_REACTIVE_NARROWED',  label: 'Binding reactive-narrowed',   group: 'Intent binding lifecycle' },
  { key: 'INTENT_BINDING_MIGRATED',           label: 'Binding migrated',            group: 'Intent binding lifecycle' },
  { key: 'INTENT_BINDING_FORCE_DETACHED',     label: 'Binding force-detached',      group: 'Intent binding lifecycle' },
  // ── Runtime source flip (1) — V214 Phase 2C ──────────────────
  { key: 'INTENT_RUNTIME_SOURCE_CHANGED',     label: 'Intent runtime-source flipped', group: 'Intent binding lifecycle' },
  // ── Agent lifecycle (12) — V220 Fix 3b ───────────────────────
  // Mirrored from audit_entries via AuditService.logEvent so the
  // hash-chained chain surfaces end-to-end agent activity (create,
  // skill add, eval, phase advance, review, publish, delete) next to
  // policy decisions. See V220__agent_lifecycle_audit_events.sql.
  { key: 'AGENT_CREATED',                     label: 'Agent created',                 group: 'Agent lifecycle' },
  { key: 'AGENT_DELETED',                     label: 'Agent deleted',                 group: 'Agent lifecycle' },
  { key: 'AGENT_PUBLISHED',                   label: 'Agent published',               group: 'Agent lifecycle' },
  { key: 'AGENT_PHASE_ADVANCED',              label: 'Agent phase advanced',          group: 'Agent lifecycle' },
  { key: 'AGENT_SKILL_ADDED',                 label: 'Agent skill added',             group: 'Agent lifecycle' },
  { key: 'AGENT_SKILL_AUTO_CREATED',          label: 'Agent skill auto-created',      group: 'Agent lifecycle' },
  { key: 'AGENT_SKILL_UNPUBLISHED',           label: 'Agent skill unpublished',       group: 'Agent lifecycle' },
  { key: 'SKILL_ADVANCED',                    label: 'Skill phase advanced',          group: 'Agent lifecycle' },
  { key: 'SKILLS_PROMOTED_TO_ACT',            label: 'Skills promoted to Act',        group: 'Agent lifecycle' },
  { key: 'SKILL_REVIEWED',                    label: 'Skill reviewed',                group: 'Agent lifecycle' },
  { key: 'EVAL_SUITE_GENERATED',              label: 'Eval suite generated',          group: 'Agent lifecycle' },
  { key: 'EVAL_RUN_COMPLETED',                label: 'Eval run completed',            group: 'Agent lifecycle' },
  // ── ExteLet registration lifecycle (P7J-RUN-023) ─────────────
  // Mirrored from ExternalAgentRegistration verify/suspend/revoke
  // probe events. Operators auditing ExteLet activity (per P7J
  // AGT-P7J-135) need these chips to filter the chain to just the
  // external-agent lifecycle. Backend emits these via AuditService.
  { key: 'AGENT_REGISTERED',                  label: 'Agent registered (ExteLet)',    group: 'ExteLet registration' },
  { key: 'AGENT_VERIFIED',                    label: 'Agent verified',                group: 'ExteLet registration' },
  { key: 'AGENT_VERIFY_FAILED',               label: 'Agent verify failed',           group: 'ExteLet registration' },
  { key: 'AGENT_SUSPENDED',                   label: 'Agent suspended',               group: 'ExteLet registration' },
  { key: 'AGENT_REVOKED',                     label: 'Agent revoked',                 group: 'ExteLet registration' },
  { key: 'AGENT_PROBE_ISSUED',                label: 'Registration probe issued',     group: 'ExteLet registration' },
  { key: 'SKILL_MAPPING_CHANGED',             label: 'Skill mapping changed',         group: 'ExteLet registration' },
  { key: 'GATEWAY_INVOCATION_SEALED',         label: 'Gateway invocation sealed',     group: 'ExteLet registration' },
  // ── Forms (form_task + external_form) — RC-228 ───────────────
  // Mirrored from ComplianceActivitiesImpl audit emissions. Operators
  // auditing form assignment/submission lifecycle (including Shape A
  // EXTERNAL_SUBJECT round-trips and DYNAMIC expression resolution)
  // filter to this group. Order roughly follows the lifecycle:
  // assignment → status → escalation → reassign → submission, plus the
  // DYNAMIC + EXTERNAL_SUBJECT specialised events.
  { key: 'FORM_TASK_ASSIGNED',                  label: 'Form task assigned',          group: 'Forms' },
  { key: 'FORM_TASK_STATUS_CHANGED',            label: 'Form task status changed',    group: 'Forms' },
  { key: 'FORM_TASK_ESCALATED',                 label: 'Form task escalated',         group: 'Forms' },
  { key: 'FORM_TASK_REASSIGNED_MEMBER_DEPARTED', label: 'Form task reassigned (member departed)', group: 'Forms' },
  { key: 'FORM_TASK_SUBMITTED',                 label: 'Form task submitted',         group: 'Forms' },
  { key: 'FORM_TASK_DYNAMIC_RESOLVED',          label: 'Form task DYNAMIC resolved',  group: 'Forms' },
  { key: 'EXTERNAL_FORM_PAUSE_REGISTERED',      label: 'External form pause registered', group: 'Forms' },
];

/**
 * RC-228 — Preset filter for the Forms group. Operators chasing a form-
 * task lifecycle through the audit chain hit this preset to collapse
 * the 7-event Forms group into one click. Lives alongside the existing
 * facet picker — selecting the preset just bulk-sets the eventType
 * facet to all 7 keys; clearing it is the same Clear button operators
 * already know.
 *
 * Keep this list in lockstep with the 'Forms' group in
 * EVENT_TYPE_OPTIONS above. Drift would mean the preset hides events
 * that are still emitted but not in the preset (silently incomplete).
 */
const FORMS_PRESET_EVENT_TYPES = [
  'FORM_TASK_ASSIGNED',
  'FORM_TASK_STATUS_CHANGED',
  'FORM_TASK_ESCALATED',
  'FORM_TASK_REASSIGNED_MEMBER_DEPARTED',
  'FORM_TASK_SUBMITTED',
  'FORM_TASK_DYNAMIC_RESOLVED',
  'EXTERNAL_FORM_PAUSE_REGISTERED',
];
const OUTCOME_OPTIONS = [
  { key: 'ALLOWED',   label: 'Allowed',   tone: 'success' },
  { key: 'BLOCKED',   label: 'Blocked',   tone: 'danger'  },
  { key: 'WARN',      label: 'Warn',      tone: 'warning' },
  { key: 'REDACTED',  label: 'Redacted',  tone: 'warning' },
  { key: 'ESCALATED', label: 'Escalated', tone: 'warning' },
  { key: 'LOGGED',    label: 'Logged',    tone: 'cyan'    },
  { key: 'FAILED',    label: 'Failed',    tone: 'danger'  },
];
const OUTCOME_TONE = Object.fromEntries(OUTCOME_OPTIONS.map(o => [o.key, o.tone]));

const ACTOR_OPTIONS = [
  { key: 'user',   label: 'User' },
  { key: 'agent',  label: 'Agent' },
  { key: 'system', label: 'System' },
];

const TIME_OPTIONS = [
  { key: '1h',   label: 'Last hour',    hours: 1     },
  { key: '24h',  label: 'Last 24h',     hours: 24    },
  { key: '7d',   label: 'Last 7 days',  hours: 168   },
  { key: '30d',  label: 'Last 30 days', hours: 720   },
  /* G334 — V1 WorkflowAuditPage offered a 90-day window. Operators
   * doing quarterly compliance reviews need this; default-30 cut
   * them off mid-period. */
  { key: '90d',  label: 'Last 90 days', hours: 2160  },
];

/* G334 — Centralised hours lookup so the reload + verify paths can't
 * drift. Previously two distinct ternary chains computed hours from
 * timeWindow strings; adding a new option meant editing both sites. */
function hoursFromWindow(key) {
  const t = TIME_OPTIONS.find((o) => o.key === key);
  return t ? t.hours : 24;
}

function fmtTime(t) {
  if (!t) return '—';
  const d = new Date(t);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString();
}

/* G334 — Source classifier. V1 WorkflowAuditPage surfaced a
 * NATIVE/EXTERNAL badge per run so operators could see at a glance
 * which side of the trust boundary fired the event. The policy audit
 * trail stores enough provenance (agentClassification, isExternal,
 * actor agent name with the AutoLets-/ExteLets-/SysLets- prefix) to
 * derive the same classification here. Falls back to 'unknown' so
 * facet counts don't conflate unclassified with native. */
function sourceOf(e) {
  if (e == null) return 'unknown';
  if (e.agentClassification === 'EXTERNAL' || e.isExternal === true || e.external === true) return 'EXTERNAL';
  if (typeof e.actorAgentName === 'string' && e.actorAgentName.startsWith('ExteLets-')) return 'EXTERNAL';
  // BUGS_COWORK_E2E B8 — honor the event's own provenance stamp in details.source when the
  // top-level classification fields aren't populated (the governed LLM proxy stamps
  // source=EXTERNAL; container ingest stamps AGENTLET_CONTAINER). Full per-event
  // agentClassification enrichment on the backend query is the remaining B8 tail.
  const ds = e.details?.source ?? e.details?.containerDetails?.source;
  if (typeof ds === 'string' && (ds === 'EXTERNAL' || ds.startsWith('AGENTLET_CONTAINER'))) return 'EXTERNAL';
  if (e.agentClassification === 'NATIVE'   || e.isNative   === true || e.agentId) return 'NATIVE';
  return 'unknown';
}

function AuditTrailPageV2Inner() {
  const navigate = useNavigate();
  const { addToast } = useToast();
  /* DEF-FE-AUDITOR-EXPORT-001 — Auditor role is "ZERO writes, ZERO exports"
   * per V208 §3 row 11 + CLAUDE_PROJECT_RULES §4.1. Auditors do their
   * forensic work in the UI; sanitized evidence egress flows through the
   * Compliance Center reports/certificates path (which redacts PII at the
   * Compliance/PA tier). Gate the CSV export by an explicit audit.export
   * capability — which Auditor does NOT hold and the wildcard roles do.
   * Backend already protects the raw-events endpoint by audit.read scope
   * but the egress format itself should be UX-restricted. */
  const { hasPermission, currentRole } = useAuth();
  const canExportAudit = !!hasPermission && (
    hasPermission('audit.export') || currentRole !== 'Auditor'
  );
  const [searchParams, setSearchParams] = useSearchParams();

  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  /* G334 — Surface reload failures. The previous implementation
   * called setLoading(false) in a `finally` but swallowed every
   * exception, leaving the operator staring at "No audit events"
   * when in reality the API had errored. Track errors explicitly
   * and render a banner above the empty state so the diagnosis is
   * obvious instead of a false negative. */
  const [error, setError] = useState(null);
  /* G344 — totalEvents tracks the server-side count for the current
   * time window (Spring Pageable.totalElements), not the page-capped
   * `events.length`. This is what the batched-verify planner uses to
   * decide whether the single-shot scan is safe or the window needs
   * to be sliced. At enterprise scale events.length is bounded by the
   * 200-row page size while totalElements can be millions. */
  const [totalEvents, setTotalEvents] = useState(0);
  /* Window totals for the KPI cards — see the `kpis` memo for why the page cannot
   * supply them. Fetched beside the trail; a failure leaves it null and the cards fall
   * back to the page with fromWindow=false so the label can stay honest. */
  const [summary, setSummary] = useState(null);
  const [integrity, setIntegrity] = useState(null);
  /* G186 — Forensic review modal state. Opens on chain-break to show the
   * full chainBreaks array from verifyAuditIntegrity with operator-actionable
   * details (event id, prev hash, current hash, computed hash, position). */
  const [forensicOpen, setForensicOpen] = useState(false);
  const [forensicReVerifying, setForensicReVerifying] = useState(false);
  /* G334 — Forensic break render limit. When the chain is badly
   * broken (e.g. a backfill run with a wrong hash key), the backend
   * can emit thousands of cascading break entries. Rendering all
   * 19k in a modal locks the browser. Default to first 50 with a
   * "Show all" escape hatch; the export-evidence button still
   * captures the full payload regardless. */
  const FORENSIC_PAGE_CAP = 50;
  const [forensicShowAll, setForensicShowAll] = useState(false);

  /* G334 — Initial time window honours ?range= deep-link from
   * external pages (Canvas, Trust Timeline, governance dashboards)
   * so a one-click drill-in lands on the right window. Falls back
   * to 24h for the default global audit view. */
  const [timeWindow, setTimeWindow] = useState(() => {
    const r = searchParams.get('range');
    return TIME_OPTIONS.some((o) => o.key === r) ? r : '24h';
  });

  /* G334 — Free-text search across event payload, agent/user IDs,
   * resource names, and event-hash prefix. V1 lacked search; for a
   * production audit surface the operator must be able to grep for
   * "0xabc123" or a specific resource. */
  const [searchText, setSearchText] = useState(() => searchParams.get('q') || '');

  /* G334 — Workflow ID filter. Canvas → "Audit & Runs" deep-links
   * with ?workflowId=<uuid>; the audit page must filter to that
   * workflow's events. V1 had this as a free-text input; V2 inherits
   * it from the URL but also exposes a chip the operator can clear. */
  const [workflowIdFilter, setWorkflowIdFilter] = useState(() => searchParams.get('workflowId') || '');

  /* RC-40 — Run ID filter. WorkflowIntelligence "Slowest runs" / "Runs"
   * sub-tabs deep-link with ?runId=<uuid>. Pre-fix the link sent the
   * runId via `q` (free-text search) which never matched policy events
   * because most workflow runs don't emit anything to policy_audit
   * (only inference + governance decisions do). The page now ALSO
   * loads workflow-lifecycle audit events from
   * /api/v1/workflows/runs/{runId}/audit when this is set, and merges
   * them into the events list — so an operator clicking "Audit" on a
   * specific Stress run sees executed/completed/etc. events for it
   * instead of an empty page. */
  const [runIdFilter, setRunIdFilter] = useState(() => searchParams.get('runId') || '');

  /* Agent scope. An agent's Audit tab ("Open Audit trail") deep-links with
   * ?agentId=<uuid>; the trail must then show ONLY that agent's governed
   * events (the same policy_audit_events the agent's integrity widget
   * verifies) rather than the tenant-wide firehose. getPolicyAuditTrail
   * already filters server-side on agentId, so we thread it straight
   * through — no client-side post-filter. */
  const [agentIdFilter, setAgentIdFilter] = useState(() => searchParams.get('agentId') || '');

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const hours = hoursFromWindow(timeWindow);
      const res = await api.getPolicyAuditTrail?.({
        hours, page: 0, size: 200,
        agentId: agentIdFilter || undefined,
      });
      /* G222 — Backend wraps results in Spring Pageable shape `{content, pageable,
       * totalElements, totalPages}`. Earlier code only checked items/events/data and
       * silently returned [] for 144KB of real audit data, making the page look empty. */
      const items = Array.isArray(res?.data?.content) ? res.data.content
                  : Array.isArray(res?.content) ? res.content
                  : Array.isArray(res?.data?.items) ? res.data.items
                  : Array.isArray(res?.items) ? res.items
                  : Array.isArray(res?.data?.events) ? res.data.events
                  : Array.isArray(res?.data) ? res.data
                  : Array.isArray(res) ? res : [];

      /* RC-40 — When the URL scopes to a specific run, fetch its
       * workflow-lifecycle audit events from workflow_audit_logs and
       * merge them in. policy_audit_events alone is empty for any
       * workflow that doesn't fire inference / governance decisions
       * (e.g. data-only workflows, the [Stress] fixtures), so the
       * page used to land empty when the operator clicked "Audit"
       * from a Workflow Intelligence row. Two-source merge gives the
       * complete picture — lifecycle (executed / completed / failed)
       * + policy chain (allow / warn / block) — sorted by createdAt.
       *
       * Normalise fields so the existing renderer/searchMatch keep
       * working without per-source branches:
       *   action          → eventType
       *   createdAt       → createdAt (already correct)
       *   details         → kept on the row for drill-in display
       *   source='lifecycle' marks the row so we can show a chip if
       *   useful later. */
      let merged = items;
      if (runIdFilter) {
        try {
          const lifecycleResp = await api.getRunAuditLog?.(runIdFilter);
          const lifecycleArr = Array.isArray(lifecycleResp?.data) ? lifecycleResp.data
                              : Array.isArray(lifecycleResp) ? lifecycleResp : [];
          const normalised = lifecycleArr.map(e => ({
            ...e,
            eventType: e.eventType || e.action,
            source: 'lifecycle',
            // runId is already on the row from BE; keep workflowId too
            // for facet/searchMatch to pick up.
          }));
          merged = [...normalised, ...items].sort((a, b) => {
            const at = new Date(a.createdAt || 0).getTime();
            const bt = new Date(b.createdAt || 0).getTime();
            return bt - at;
          });
        } catch (lifecycleErr) {
          /* Don't fail the whole page if the lifecycle fetch errors —
           * surface the warning, keep the policy events. The empty
           * state below will still render if both are empty. */
          console.warn('RC-40: lifecycle audit fetch failed for run', runIdFilter, lifecycleErr);
        }
      }
      setEvents(merged);
      /* G344 — Pageable.totalElements is the unfiltered tenant-wide
       * count for the time window; falls back to items.length when
       * the backend returns a plain array. Drives the batched-verify
       * planner. */
      const tot = res?.data?.totalElements ?? res?.totalElements
               ?? res?.data?.totalItems ?? res?.totalItems
               ?? items.length;
      setTotalEvents(Number(tot) || 0);

      try {
        const sres = await api.getPolicyAuditSummary?.({
          hours, agentId: agentIdFilter || undefined,
        });
        setSummary(sres?.data || sres || null);
      } catch (_) {
        /* Non-fatal: the trail still renders and the cards degrade to page counts. */
        setSummary(null);
      }
    } catch (err) {
      /* G334 — Capture the failure for the banner and notify the
       * operator via toast. The events table will fall back to the
       * empty state below the banner. */
      setError(err?.message || String(err) || 'Failed to load audit trail');
      setEvents([]);
      addToast({ tone: 'danger', title: 'Audit trail load failed', description: err?.message || 'Try refresh; if it persists, check API health.' });
    } finally {
      setLoading(false);
    }
  // addToast is a stable callback from useToast; intentionally omit
  // to avoid reload thrashing.
  // eslint-disable-next-line
  }, [timeWindow, runIdFilter, agentIdFilter]);
  useEffect(() => { reload(); }, [reload]);

  /* G186 — Normalize the verifyAuditIntegrity response. Backend returns
   *   { valid, eventsChecked, timeRange, chainBreaks?: [...] }
   * but earlier V2 code read intact / verified / breakCount, which never
   * matched the wire format. Map the real shape onto a stable internal
   * struct the rest of this component already references.
   *
   * G334 — Parse string-form breaks into structured objects.
   *
   * Backend PolicyAuditService.verifyIntegrity emits `chainBreaks` as
   * `List<String>` with one of two human-readable formats:
   *   "Chain break at event <UUID> (index N): expected previousHash='X', found='Y'"
   *   "Hash mismatch at event <UUID> (index N): computed='X', stored='Y'"
   *
   * The V2 forensic modal expected objects with eventId/position/
   * expectedHash/actualHash fields and silently rendered blank rows
   * when handed bare strings. We parse both formats into a stable
   * object shape so the modal can render the same per-break detail
   * (and the "Open event" drill-in can still find the event by id)
   * irrespective of which break category fired. Any unparseable
   * line falls back to verbatim display so an operator never loses
   * forensic context. */
  const parseChainBreak = (entry, idx) => {
    if (entry && typeof entry === 'object') {
      /* If the backend ever upgrades to an object payload, pass it
       * through unchanged so we don't double-wrap. */
      return entry;
    }
    if (typeof entry !== 'string') return { idx, message: String(entry ?? '') };
    const chain = /^Chain break at event ([0-9a-f-]+) \(index (\d+)\):\s*expected previousHash='([^']*)',\s*found='([^']*)'/i.exec(entry);
    if (chain) {
      return {
        idx,
        kind: 'chain',
        eventId: chain[1],
        position: Number(chain[2]),
        expectedHash: chain[3],
        actualHash: chain[4],
        message: `Previous-hash link does not match the prior event's recorded hash.`,
      };
    }
    const hash = /^Hash mismatch at event ([0-9a-f-]+) \(index (\d+)\):\s*computed='([^']*)',\s*stored='([^']*)'/i.exec(entry);
    if (hash) {
      return {
        idx,
        kind: 'hash',
        eventId: hash[1],
        position: Number(hash[2]),
        expectedHash: hash[3],  // re-computed value the chain says it should be
        actualHash:   hash[4],  // value stored at rest — the tampered side
        message: `Event payload hash does not match the value computed from its inputs.`,
      };
    }
    return { idx, message: entry };
  };

  const normalizeIntegrity = (raw) => {
    const data = raw?.data || raw || {};
    /* Prefer the server's structured breaks. The prose form and its regex parser remain
     * as a fallback for a response that predates them, but a kind recovered by matching
     * English is a kind that silently becomes 'unparseable' the day the wording changes. */
    const chainBreaks = hasStructuredBreaks(data)
      ? data.breaks.map(adaptBreak)
      : (Array.isArray(data.chainBreaks) ? data.chainBreaks : []).map(parseChainBreak);
    return {
      intact: data.valid !== false && chainBreaks.length === 0,
      verified: data.eventsChecked ?? data.events ?? data.verified ?? 0,
      events: data.eventsChecked ?? data.events ?? 0,
      breakCount: chainBreaks.length,
      chainBreaks,
      timeRange: data.timeRange || null,
      /* Which chain this verdict is about, and whether anything was actually altered —
       * the two things the old boolean could not say. */
      scope: data.scope ?? null,
      scopeLabel: data.scopeLabel ?? null,
      contentUnaltered: data.contentUnaltered === true,
      fullyRepairable: data.fullyRepairable === true,
    };
  };

  /* G344 — Batched-verify orchestrator.
   *
   * Why batches: at enterprise scale a single tenant can hold millions
   * of audit events in a 30-day window. The original single-shot scan
   * pegs the database, times out at the gateway, and returns tens of
   * thousands of cascading break entries that swamp the UI. The
   * batched path slices the time window into ~100-event chunks,
   * verifies each chunk in turn, and streams progress + accumulated
   * breaks into the UI so the operator can see what's happening,
   * cancel, or stop early once they have enough forensic evidence.
   *
   * Sizing: BATCH_TARGET_EVENTS is the desired upper bound per slice;
   * total batches = ceil(totalEvents / target). When totalEvents is
   * below the threshold we use the legacy single-shot path to avoid
   * burning RPC overhead on small windows.
   *
   * Chain-continuity caveat: the backend's verifier resets its
   * `expectedPreviousHash` at i==0 within each batch (PolicyAuditService
   * line 683-687), so a break sitting exactly on the batch boundary
   * would be missed. We mitigate by overlapping consecutive slices
   * by 1 second of wall time — events on the boundary therefore
   * appear in BOTH batches, and a real break is still caught.
   *
   * Cancellation: a per-run AbortController is forwarded to apiFetch;
   * cancelling stops the next batch from firing and propagates a
   * proper AbortError to any in-flight request. */
  const BATCH_TARGET_EVENTS = 100;
  const BATCH_THRESHOLD     = 100;
  const [verifyProgress, setVerifyProgress] = useState(null);
  /* { mode: 'single'|'batched', current, total, eventsScanned, breaks, status }
   * status ∈ 'running' | 'complete' | 'cancelled' | 'error' */
  const verifyAbortRef = useRef(null);

  const buildBatches = (totalHours, batchCount) => {
    /* Slice the window into batchCount equal-duration ISO instants,
     * with 1-second overlap to cover the boundary chain-link.
     * Returns the OLDEST batch first so the operator sees historic
     * events get verified before recent ones (most operators want
     * "is the chain still healthy" — recent first matters more for
     * incident response). We reverse at the end. */
    const now = Date.now();
    const startMs = now - totalHours * 3600 * 1000;
    const sliceMs = (totalHours * 3600 * 1000) / batchCount;
    const overlap = 1000; // 1s
    const out = [];
    for (let i = 0; i < batchCount; i++) {
      const fromMs = Math.max(startMs, startMs + i * sliceMs - (i > 0 ? overlap : 0));
      const toMs   = i === batchCount - 1 ? now : startMs + (i + 1) * sliceMs;
      out.push({
        fromInstant: new Date(fromMs).toISOString(),
        toInstant:   new Date(toMs).toISOString(),
      });
    }
    /* Newest batch first — surfaces fresh breaks fastest. */
    return out.reverse();
  };

  const handleVerifyIntegrity = async () => {
    const totalHours = hoursFromWindow(timeWindow);
    /* If the window is small or we don't know the count yet, the
     * single-shot path is fastest. */
    if (!totalEvents || totalEvents <= BATCH_THRESHOLD) {
      setVerifyProgress({ mode: 'single', current: 0, total: 1, eventsScanned: 0, breaks: 0, status: 'running' });
      try {
        const res = await api.verifyAuditIntegrity?.(
          buildVerifyArgs({ hours: totalHours, agentId: agentIdFilter }));
        const data = normalizeIntegrity(res);
        setIntegrity(data);
        setVerifyProgress({ mode: 'single', current: 1, total: 1, eventsScanned: data.events, breaks: data.breakCount, status: 'complete' });
        addToast({
          tone: data.intact ? 'success' : 'danger',
          title: data.intact ? 'Audit chain intact' : 'Audit chain BREAK detected',
          description: data.intact
            ? `${data.verified} events verified.`
            : `${data.breakCount} break(s) — opening forensic review.`,
        });
        if (!data.intact) {
          setForensicShowAll(false);
          setForensicOpen(true);
        }
      } catch (err) {
        setVerifyProgress({ mode: 'single', current: 0, total: 1, eventsScanned: 0, breaks: 0, status: 'error' });
        addToast({ tone: 'danger', title: 'Verification failed', description: err?.message });
      }
      return;
    }

    /* Batched path. */
    const batchCount = Math.max(2, Math.min(50, Math.ceil(totalEvents / BATCH_TARGET_EVENTS)));
    const batches = buildBatches(totalHours, batchCount);
    const controller = new AbortController();
    verifyAbortRef.current = controller;
    const aggregateBreaks = [];
    let aggregateEvents = 0;
    setVerifyProgress({ mode: 'batched', current: 0, total: batchCount, eventsScanned: 0, breaks: 0, status: 'running' });
    addToast({
      tone: 'cyan',
      title: 'Batched verification started',
      description: `${totalEvents.toLocaleString()} events split into ${batchCount} batches (≈${BATCH_TARGET_EVENTS}/batch).`,
    });

    let cancelled = false;
    for (let i = 0; i < batches.length; i++) {
      if (controller.signal.aborted) { cancelled = true; break; }
      const { fromInstant, toInstant } = batches[i];
      try {
        const res = await api.verifyAuditIntegrity?.(buildVerifyArgs({
          hours: totalHours,
          agentId: agentIdFilter,
          fromInstant, toInstant,
          signal: controller.signal,
        }));
        const slice = normalizeIntegrity(res);
        aggregateEvents += slice.events || 0;
        if (Array.isArray(slice.chainBreaks)) {
          for (const br of slice.chainBreaks) aggregateBreaks.push(br);
        }
        setVerifyProgress({
          mode: 'batched',
          current: i + 1,
          total: batchCount,
          eventsScanned: aggregateEvents,
          breaks: aggregateBreaks.length,
          status: 'running',
        });
      } catch (err) {
        if (err?.name === 'AbortError' || controller.signal.aborted) {
          cancelled = true;
          break;
        }
        /* G344 — Partial failure: surface to the operator but keep
         * going. A single 5xx on one slice shouldn't lose the rest
         * of the scan; we record the failure as a synthetic break
         * line so the audit pack reflects the gap. */
        aggregateBreaks.push(`Batch ${i + 1}/${batchCount} from ${fromInstant} to ${toInstant} FAILED: ${err?.message || err}`);
        setVerifyProgress({
          mode: 'batched',
          current: i + 1,
          total: batchCount,
          eventsScanned: aggregateEvents,
          breaks: aggregateBreaks.length,
          status: 'running',
        });
      }
    }

    /* Synthesise an aggregate integrity result that looks identical
     * to a single-shot response so downstream rendering doesn't need
     * to know batching happened. */
    const final = {
      intact: !cancelled && aggregateBreaks.length === 0,
      verified: aggregateEvents,
      events: aggregateEvents,
      breakCount: aggregateBreaks.length,
      chainBreaks: aggregateBreaks.map(parseChainBreak),
      timeRange: null,
    };
    setIntegrity(final);
    setVerifyProgress({
      mode: 'batched',
      current: batchCount,
      total: batchCount,
      eventsScanned: aggregateEvents,
      breaks: aggregateBreaks.length,
      status: cancelled ? 'cancelled' : 'complete',
    });
    verifyAbortRef.current = null;
    if (cancelled) {
      addToast({
        tone: 'warning',
        title: 'Verification cancelled',
        description: `Partial result: ${aggregateEvents.toLocaleString()} events scanned, ${aggregateBreaks.length} break(s) so far.`,
      });
    } else {
      addToast({
        tone: final.intact ? 'success' : 'danger',
        title: final.intact ? 'Chain intact across all batches' : 'Chain BREAK detected',
        description: final.intact
          ? `${aggregateEvents.toLocaleString()} events verified across ${batchCount} batches.`
          : `${aggregateBreaks.length} break(s) across ${batchCount} batches — opening forensic review.`,
      });
      if (!final.intact) {
        setForensicShowAll(false);
        setForensicOpen(true);
      }
    }
  };

  const cancelVerify = () => {
    try { verifyAbortRef.current?.abort(); } catch (_) { /* no-op */ }
  };

  /* G345 — Repair chain action. Calls the backend rebuild endpoint
   * for the current time window so the operator can heal events that
   * were written before the timestamp-precision fix. Confirmation
   * prompt because this mutates audit data — usually a one-shot
   * remediation, not a routine operation. */
  const [repairRunning, setRepairRunning] = useState(false);
  // Inline confirm (platform-native danger+ghost, no browser window.confirm).
  const [repairConfirm, setRepairConfirm] = useState(false);
  const handleRepairChain = async () => {
    setRepairConfirm(false);
    setRepairRunning(true);
    try {
      const hours = hoursFromWindow(timeWindow);
      const res = await api.rebuildAuditChain({ hours });
      const data = res?.data || res || {};
      addToast({
        tone: 'success',
        title: 'Chain repaired',
        description: `${data.eventsScanned || 0} events scanned, ${data.hashesRewritten || 0} hashes rewritten. Run Verify integrity to confirm.`,
      });
      /* Auto-trigger a fresh verify so the operator sees the green
       * banner without an extra click. */
      setIntegrity(null);
      await handleVerifyIntegrity();
    } catch (err) {
      addToast({
        tone: 'danger',
        title: 'Chain repair failed',
        description: err?.message || 'Backend rejected the rebuild request.',
      });
    } finally {
      setRepairRunning(false);
    }
  };

  /* Facets — G334 adds 'source' (NATIVE/EXTERNAL/unknown) so the
   * operator can scope the audit table to one side of the trust
   * boundary. The actor facet stays separate because actor is
   * orthogonal to source (a NATIVE agent's event can be triggered
   * by a USER actor or a SYSTEM actor). */
  const SOURCE_OPTIONS = useMemo(() => ([
    { key: 'NATIVE',   label: 'Native (Lets)' },
    { key: 'EXTERNAL', label: 'External (ExteLets)' },
    { key: 'unknown',  label: 'Unclassified' },
  ]), []);
  const [facets, setFacets] = useState(() => {
    /* G334 — Honour URL-driven facet pre-selection so deep links from
     * Trust Timeline or Compliance Center land on the right slice. */
    const fromParam = (p) => {
      const v = searchParams.get(p);
      return v ? new Set(v.split(',').filter(Boolean)) : new Set();
    };
    return {
      eventType: fromParam('type'),
      outcome:   fromParam('outcome'),
      actor:     fromParam('actor'),
      source:    fromParam('source'),
    };
  });
  const toggleFacet = (gKey, oKey, c) => setFacets(p => { const n = new Set(p[gKey] || []); c ? n.add(oKey) : n.delete(oKey); return { ...p, [gKey]: n }; });
  const clearFacets = () => setFacets({ eventType: new Set(), outcome: new Set(), actor: new Set(), source: new Set() });

  // RC-228 — Forms preset: one-click bulk-set the eventType facet to the
  // 7-event Forms group. Other facets are reset so the preset gives a
  // clean focused view ("show me everything Form-related, regardless of
  // outcome / actor / source"). Operators who want to narrow further
  // can then toggle individual chips. The existing Clear-all button
  // exits the preset (no new exit affordance needed).
  const applyFormsPreset = () => setFacets({
    eventType: new Set(FORMS_PRESET_EVENT_TYPES),
    outcome:   new Set(),
    actor:     new Set(),
    source:    new Set(),
  });

  // True when the eventType facet currently equals the Forms preset
  // exactly (same size + all keys match). Used to render the preset
  // button in an "active" state so operators see they're inside the
  // preset view.
  const formsPresetActive = (
    facets.eventType.size === FORMS_PRESET_EVENT_TYPES.length
    && FORMS_PRESET_EVENT_TYPES.every((k) => facets.eventType.has(k))
  );

  const FACET_GROUPS = useMemo(() => ([
    { key: 'eventType', label: 'Event type', options: EVENT_TYPE_OPTIONS },
    { key: 'outcome',   label: 'Outcome',    options: OUTCOME_OPTIONS.map(o => ({ key: o.key, label: o.label })) },
    { key: 'actor',     label: 'Actor',      options: ACTOR_OPTIONS },
    { key: 'source',    label: 'Source',     options: SOURCE_OPTIONS },
  ]), [SOURCE_OPTIONS]);

  /* G222 — Backend stores eventType + decision as uppercase strings. Facet keys
   * already match those (POLICY_RULE_EVALUATION / ALLOWED etc), so compare raw. */
  const outcomeOf = (e) => (e?.outcome || e?.decision || '').toUpperCase();
  const eventTypeOf = (e) => (e?.eventType || e?.type || '').toUpperCase();
  const actorOf = (e) => e?.actorType || (e?.userId || e?.actorId ? 'user' : e?.agentId ? 'agent' : 'system');

  /* G334 — Set of event IDs surfaced by the last integrity verify as
   * being in a chain break. Used to render the per-row integrity dot
   * so the operator can see which events are tampered or downstream
   * of a tamper, not just the aggregate "BREAK DETECTED" banner. */
  const brokenEventIds = useMemo(() => {
    const s = new Set();
    if (integrity && Array.isArray(integrity.chainBreaks)) {
      for (const br of integrity.chainBreaks) {
        if (br?.eventId) s.add(br.eventId);
      }
    }
    return s;
  }, [integrity]);

  /* G334 — Search text matches a small set of fields likely to be
   * grepped by an investigator: event/previous hash prefix, actor IDs,
   * resource name, eventType, decision, workflow name. Case-insensitive
   * substring; cheap because event count per window is bounded by the
   * 200-row page-size cap. */
  const searchMatch = (e, q) => {
    if (!q) return true;
    const needle = q.toLowerCase();
    const hay = [
      e.eventHash, e.previousEventHash, e.previousHash, e.hash,
      e.userId, e.agentId, e.actorId, e.actorAgentName,
      e.resourceName, e.skillName, e.workflowName, e.workflowId,
      // RC-40 — workflow run identifiers. Without these, a `?q=<runId>`
      // free-text search matched zero rows even when the workflow_audit_logs
      // events for that run carried runId on every row. Both casing variants
      // appear depending on whether the row came from policy_audit or
      // workflow_audit_logs (different BE entities, slightly different DTOs).
      e.runId, e.workflowRunId,
      e.eventType, e.type, e.outcome, e.decision,
      e.summary, e.action, e.description,
    ].filter(Boolean).map(String).join(' ').toLowerCase();
    return hay.includes(needle);
  };

  const filtered = useMemo(() => events.filter(e => {
    if (facets.eventType.size > 0 && !facets.eventType.has(eventTypeOf(e))) return false;
    if (facets.outcome.size > 0 && !facets.outcome.has(outcomeOf(e))) return false;
    if (facets.actor.size > 0 && !facets.actor.has(actorOf(e))) return false;
    if (facets.source && facets.source.size > 0 && !facets.source.has(sourceOf(e))) return false;
    /* G397 — Only reject when the event explicitly carries a non-matching
     * workflowId. Events loaded via /api/v1/workflows/{id}/audit (the
     * canvas "Audit & Runs" deep-link path) come back scoped via URL and
     * frequently OMIT workflowId on each row — the scoping is implicit.
     * Pre-G397 the check `(e.workflowId || '') !== workflowIdFilter`
     * compared an empty string against the UUID and filtered EVERY event
     * out, producing the user-reported empty grid with the 3 events
     * still aggregating in the sidebar counts. */
    if (workflowIdFilter && e.workflowId && e.workflowId !== workflowIdFilter) return false;
    if (!searchMatch(e, searchText)) return false;
    return true;
  }), [events, facets, workflowIdFilter, searchText]);

  /* G175 — Pagination over the filtered list. Reset to page 1 whenever the
   * filtered length changes so the user doesn't land on an empty page. */
  const pg = usePaginationState(10);
  const visibleRows = pg.paginate(filtered);
  useEffect(() => { pg.setPage(0); }, [filtered.length]);

  const valueFor = (e, key) =>
      key === 'eventType' ? eventTypeOf(e)
    : key === 'outcome'   ? outcomeOf(e)
    : key === 'actor'     ? actorOf(e)
    : key === 'source'    ? sourceOf(e)
    : e?.[key];

  /* G334 — URL deep-link sync. Persist filter + range + search +
   * workflow filter into the address bar so operators can bookmark
   * or share a specific slice of the audit trail. Uses replaceState
   * (not pushState) so the back button stays useful. */
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    const setOrDel = (k, v) => { if (v) next.set(k, v); else next.delete(k); };
    setOrDel('range', timeWindow === '24h' ? '' : timeWindow);
    setOrDel('q', searchText);
    setOrDel('workflowId', workflowIdFilter);
    // RC-40 — keep ?runId= in sync so "Clear scope" + bookmarks behave.
    setOrDel('runId', runIdFilter);
    // Keep ?agentId= in sync so the agent-scope chip's "Clear scope" + bookmarks behave.
    setOrDel('agentId', agentIdFilter);
    setOrDel('type',    [...(facets.eventType || [])].join(',') || '');
    setOrDel('outcome', [...(facets.outcome   || [])].join(',') || '');
    setOrDel('actor',   [...(facets.actor     || [])].join(',') || '');
    setOrDel('source',  [...(facets.source    || [])].join(',') || '');
    /* Preserve any other params the caller threaded in (e.g. legacy). */
    setSearchParams(next, { replace: true });
    // setSearchParams reference identity changes on every render in react-router-dom;
    // depending on it would cause an infinite loop.
    // eslint-disable-next-line
  }, [timeWindow, searchText, workflowIdFilter, runIdFilter, agentIdFilter, facets]);

  const facetCounts = useMemo(() => {
    const counts = {};
    for (const g of FACET_GROUPS) {
      counts[g.key] = {};
      for (const opt of g.options) {
        counts[g.key][opt.key] = events.filter(e => {
          for (const og of FACET_GROUPS) {
            if (og.key === g.key) continue;
            const sel = facets[og.key]; if (!sel || sel.size === 0) continue;
            if (!sel.has(valueFor(e, og.key))) return false;
          }
          return valueFor(e, g.key) === opt.key;
        }).length;
      }
    }
    return counts;
  }, [events, facets, FACET_GROUPS]);

  const facetGroupsWithCounts = useMemo(() => FACET_GROUPS.map(g => ({
    ...g, options: g.options.map(o => ({ ...o, count: facetCounts[g.key]?.[o.key] || 0, checked: facets[g.key]?.has(o.key) || false })),
  })), [facetCounts, facets, FACET_GROUPS]);

  const activeFilterChips = useMemo(() => {
    const chips = [];
    for (const g of FACET_GROUPS) for (const optKey of facets[g.key] || []) {
      const opt = g.options.find(o => o.key === optKey);
      chips.push({ groupKey: g.key, optionKey: optKey, label: `${g.label}: ${opt?.label || optKey}` });
    }
    return chips;
  }, [facets, FACET_GROUPS]);

  const [density, setDensity] = useState(() => { try { return localStorage.getItem('audit-density') || 'compact'; } catch { return 'compact'; } });
  const cycleDensity = () => { const n = density === 'comfortable' ? 'compact' : 'comfortable'; setDensity(n); try { localStorage.setItem('audit-density', n); } catch {} };

  const [selected, setSelected] = useState(null);
  const [slideoutTab, setSlideoutTab] = useState('detail');
  /* G345 — Per-event hash verification state. Stored as a Map of
   * eventId → { state, computedHash, message } so a freshly opened
   * slideout starts blank and re-opening the same event remembers
   * its result. */
  const [perEventVerify, setPerEventVerify] = useState({});
  const openSlideout = (row) => { setSelected(row); setSlideoutTab('detail'); };
  const closeSlideout = () => setSelected(null);

  /**
   * G349 — Verify a single event's hash via the server-side endpoint.
   *
   * <p>An earlier implementation tried to recompute the hash in the
   * browser using Web Crypto SHA-256. That approach was abandoned
   * because JavaScript's `Date` only carries millisecond precision,
   * while the backend canonicalises the hash input to microsecond-
   * truncated ISO instants. The browser literally cannot reproduce
   * the canonical input string, so even a perfectly intact event
   * would report "Hash mismatch" — exactly the false positive we
   * caught during Chrome verification.
   *
   * <p>The server endpoint (`/audit/events/{id}/verify-hash`) loads
   * the event by ID and runs the same `computeEventHash` function
   * the chain verifier uses, guaranteeing byte-for-byte agreement
   * with the stored hash. The slideout displays the verdict + both
   * hashes for the operator's reference.
   */
  const verifyOneEvent = async (ev) => {
    if (!ev) return;
    const id = ev.id || ev.eventId;
    if (!id) return;
    setPerEventVerify((s) => ({ ...s, [id]: { state: 'verifying' } }));
    try {
      const res = await api.verifyAuditEventHash(id);
      const data = res?.data || res || {};
      const computed = data.computedHash || null;
      const stored   = data.storedHash   || ev.eventHash || ev.hash || null;
      if (!stored) {
        setPerEventVerify((s) => ({ ...s, [id]: {
          state: 'unknown',
          computedHash: computed,
          storedHash: null,
          message: data.message || 'This event has no stored hash to compare against.',
        }}));
        return;
      }
      if (data.valid === true) {
        setPerEventVerify((s) => ({ ...s, [id]: {
          state: 'valid',
          computedHash: computed,
          storedHash: stored,
        }}));
      } else {
        setPerEventVerify((s) => ({ ...s, [id]: {
          state: 'broken',
          computedHash: computed,
          storedHash: stored,
          message: data.message ||
            'Stored hash does not match the value recomputed from this event\'s inputs. ' +
            'Either the event was tampered, or it pre-dates the G345 timestamp-precision fix ' +
            '(in which case running "Repair chain" from the breaks banner will heal it).',
        }}));
      }
    } catch (err) {
      setPerEventVerify((s) => ({ ...s, [id]: { state: 'error', message: err?.message || String(err) }}));
    }
  };

  const kpis = useMemo(() => {
    /* These are window totals, served by GET /audit/summary as one GROUP BY.
     *
     * They used to be counted from `events` — the rows on the current page, a fixed 200
     * and clamped again in the API layer — and labelled "Total events". So a window
     * holding fourteen thousand reported two hundred, and ALLOWED / BLOCKED / ERRORS were
     * counts within whichever two hundred rows happened to load. On a compliance surface
     * those are the numbers being attested to. The true count was already in state
     * (`totalEvents`, from the page envelope's totalElements) the whole time.
     *
     * G222 — decisions are uppercase: ALLOWED / BLOCKED / WARN / REDACTED / ESCALATED /
     * LOGGED / FAILED. Earlier code compared lowercase and every count read 0. */
    const d = summary?.byDecision || null;
    if (d) {
      return {
        total: summary.total ?? totalEvents ?? 0,
        allows: d.ALLOWED ?? 0,
        blocks: d.BLOCKED ?? 0,
        errors: d.FAILED ?? 0,
        fromWindow: true,
      };
    }
    /* Summary not loaded (or the endpoint is older than this page). Fall back to the page
     * and SAY SO, rather than presenting a page count as a window total. */
    return {
      total: totalEvents || events.length,
      allows: events.filter(e => outcomeOf(e) === 'ALLOWED').length,
      blocks: events.filter(e => outcomeOf(e) === 'BLOCKED').length,
      errors: events.filter(e => outcomeOf(e) === 'FAILED').length,
      fromWindow: false,
    };
  }, [events, summary, totalEvents]);

  return (
    <div className="min-h-full bg-v2-page text-v2-ink p-4 sm:p-7 max-w-[1500px] mx-auto">
      <header className="flex items-start gap-4 mb-5">
        <div>
          <h1 className="text-[22px] font-bold tracking-tight m-0">Audit Trail</h1>
          <p className="text-[13px] text-v2-ink-secondary mt-0.5">
            Hash-chained, tamper-evident audit log of every governance decision · {kpis.total.toLocaleString()} events in window · showing {events.length.toLocaleString()} · {filtered.length} match filters
          </p>
        </div>
        <div className="ml-auto flex items-center gap-2 flex-wrap justify-end">
          {/* G334 — Free-text search across hash prefix, actor IDs, resource names. */}
          <label className="relative">
            <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-v2-ink-muted" />
            <input
              type="text"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="Search hash, actor, resource…"
              aria-label="Search audit events"
              className="pl-7 pr-2.5 py-1.5 text-[12px] border border-v2-border-subtle rounded-v2-sm bg-v2-surface w-[220px] focus:border-v2-action focus:outline-none"
            />
            {searchText && (
              <button onClick={() => setSearchText('')} aria-label="Clear search"
                      className="absolute right-1.5 top-1/2 -translate-y-1/2 text-v2-ink-muted hover:text-v2-ink p-0.5">
                <X size={11} />
              </button>
            )}
          </label>
          {/* Time window */}
          <div className="flex items-center gap-1 bg-v2-muted rounded-v2-pill p-1">
            {TIME_OPTIONS.map(t => (
              <button key={t.key} onClick={() => setTimeWindow(t.key)}
                className={'px-3 py-1 rounded-v2-pill text-[11.5px] font-semibold transition-colors duration-v2 ' +
                  (timeWindow === t.key ? 'bg-v2-surface text-v2-ink shadow-v2-sm' : 'text-v2-ink-muted hover:text-v2-ink')}>
                {t.label}
              </button>
            ))}
          </div>
          <Button variant="ghost" size="sm" iconLeft={<RefreshCw size={13} className={loading ? 'animate-spin' : ''} />} onClick={reload} disabled={loading}>Refresh</Button>
          <Button variant="secondary" size="sm" onClick={cycleDensity}>Density: {density.charAt(0).toUpperCase() + density.slice(1)}</Button>
          {/* G334 — CSV export of the currently-filtered table. Keeps the
              forensic JSON export available too; CSV is what compliance
              auditors paste into evidence packs.

              DEF-FE-AUDITOR-EXPORT-001 — Auditor role does NOT see this
              button per V208 §3 row 11 + CLAUDE_PROJECT_RULES §4.1
              ("zero exports"). Their evidence-egress path is the
              sanitized Compliance Center → Reports / Certificates flow,
              which redacts PII at author time. Gated on canExportAudit
              (computed at component top from hasPermission). */}
          {canExportAudit && (
          <Button
            variant="secondary"
            size="sm"
            iconLeft={<Download size={13} />}
            disabled={filtered.length === 0}
            onClick={() => {
              const cols = ['timestamp','eventType','outcome','actorType','userId','agentId','resourceName','workflowId','eventHash','previousEventHash'];
              const esc = (v) => {
                if (v == null) return '';
                const s = String(v).replace(/"/g, '""');
                return /[",\n]/.test(s) ? `"${s}"` : s;
              };
              const rows = [cols.join(',')];
              for (const e of filtered) {
                rows.push(cols.map((c) => esc(
                  c === 'actorType'  ? actorOf(e)
                : c === 'outcome'    ? outcomeOf(e)
                : c === 'eventType'  ? eventTypeOf(e)
                : c === 'timestamp'  ? (e.timestamp || e.createdAt || '')
                : e[c]
                )).join(','));
              }
              const blob = new Blob([rows.join('\n')], { type: 'text/csv' });
              const a = document.createElement('a');
              a.href = URL.createObjectURL(blob);
              a.download = `audit-${timeWindow}-${new Date().toISOString().replace(/[:.]/g,'-')}.csv`;
              a.click();
              setTimeout(() => URL.revokeObjectURL(a.href), 0);
              addToast({ tone: 'success', title: 'CSV exported', description: `${filtered.length} events written.` });
            }}
          >
            Export CSV
          </Button>
          )}
          {/* G344 — Verify button is batch-aware. When the loaded
              window has more than BATCH_THRESHOLD events, the click
              kicks off a sliced scan; otherwise it falls through to
              the legacy single-shot path. The button label surfaces
              the planned batch count so the operator isn't surprised
              when they see a progress bar instead of an instant
              result on a million-event tenant. */}
          {(() => {
            const tot = totalEvents || 0;
            const batched = tot > BATCH_THRESHOLD;
            const batchCount = batched
              ? Math.max(2, Math.min(50, Math.ceil(tot / BATCH_TARGET_EVENTS)))
              : 1;
            const label = verifyProgress?.status === 'running'
              ? `Verifying ${verifyProgress.current}/${verifyProgress.total}…`
              : batched
              ? `Verify in ${batchCount} batches`
              : 'Verify integrity';
            return (
              <Button
                variant="primary"
                size="sm"
                iconLeft={<ShieldCheck size={13} className={verifyProgress?.status === 'running' ? 'animate-spin' : ''} />}
                onClick={handleVerifyIntegrity}
                disabled={verifyProgress?.status === 'running'}
                title={batched
                  ? `Will run ${batchCount} sequential batches of ~${BATCH_TARGET_EVENTS} events. Cancellable mid-flight.`
                  : 'Verify the full chain in this window in a single request.'}
              >
                {label}
              </Button>
            );
          })()}
        </div>
      </header>

      {/* G344 — Batched-verify progress UI. Renders only while a
          verification is in flight or has just finished, with a
          cancel button operators can hit when the batches stack up
          on a heavily-broken chain. Hidden once the operator
          dismisses or starts another verify. */}
      {verifyProgress && verifyProgress.mode === 'batched' && (
        <div className={'rounded-v2-md border px-4 py-3 mb-5 ' +
          (verifyProgress.status === 'running' ? 'border-v2-action-border bg-v2-action-soft' :
           verifyProgress.status === 'cancelled' ? 'border-v2-warning-border bg-v2-warning-soft' :
           verifyProgress.status === 'error' ? 'border-v2-danger-border bg-v2-danger-soft' :
           'border-v2-success-border bg-v2-success-soft')
        }>
          <div className="flex items-center gap-3">
            {verifyProgress.status === 'running'
              ? <RefreshCw size={16} className="animate-spin text-v2-action-strong" />
              : verifyProgress.status === 'cancelled'
              ? <AlertTriangle size={16} className="text-v2-warning-strong" />
              : verifyProgress.breaks > 0
              ? <AlertTriangle size={16} className="text-v2-danger" />
              : <CheckCircle size={16} className="text-v2-success" />}
            <div className="flex-1">
              <div className="font-semibold text-[12.5px] text-v2-ink">
                {verifyProgress.status === 'running' ? 'Batched verification in progress' :
                 verifyProgress.status === 'cancelled' ? 'Verification cancelled' :
                 verifyProgress.status === 'error' ? 'Verification failed' :
                 verifyProgress.breaks > 0 ? 'Batched verification complete — breaks detected' :
                 'Batched verification complete — chain intact'}
              </div>
              <div className="text-[11.5px] text-v2-ink-secondary mt-0.5">
                Batch <strong>{verifyProgress.current}</strong> of <strong>{verifyProgress.total}</strong>
                {' · '}<strong>{verifyProgress.eventsScanned.toLocaleString()}</strong> events scanned
                {' · '}<strong className={verifyProgress.breaks > 0 ? 'text-v2-danger-strong' : 'text-v2-success-strong'}>
                  {verifyProgress.breaks.toLocaleString()}
                </strong> break{verifyProgress.breaks === 1 ? '' : 's'} so far
              </div>
              {/* Progress bar */}
              <div className="mt-2 h-1.5 bg-v2-muted rounded-full overflow-hidden">
                <div
                  className={'h-full transition-all duration-300 ' +
                    (verifyProgress.breaks > 0 ? 'bg-v2-danger' : 'bg-v2-action')}
                  style={{ width: `${verifyProgress.total > 0 ? (verifyProgress.current / verifyProgress.total) * 100 : 0}%` }}
                />
              </div>
            </div>
            {verifyProgress.status === 'running' ? (
              <Button variant="secondary" size="sm" onClick={cancelVerify}>Cancel</Button>
            ) : (
              <Button variant="ghost" size="sm" iconLeft={<X size={12} />} onClick={() => setVerifyProgress(null)}>Dismiss</Button>
            )}
          </div>
        </div>
      )}

      {/* G334 — Error banner. Previously a reload failure left the table
          empty with no diagnostic, indistinguishable from a 0-event
          window. The banner is dismissable; the operator can retry. */}
      {error && (
        <div className="rounded-v2-md border-l-4 border-v2-danger bg-v2-danger-soft px-4 py-3 mb-5 flex items-center gap-3">
          <AlertTriangle size={18} className="text-v2-danger" />
          <div className="flex-1 text-[12.5px]">
            <div className="font-bold text-v2-ink">Audit trail load failed</div>
            <div className="text-v2-ink-secondary">{error}</div>
          </div>
          <Button variant="ghost" size="sm" onClick={reload}>Retry</Button>
          <Button variant="ghost" size="sm" iconLeft={<X size={12} />} onClick={() => setError(null)}>Dismiss</Button>
        </div>
      )}

      {/* G334 — Workflow-scoped chip. Surfaces when the page was opened
          via Canvas → Audit & Runs deep-link (?workflowId=…) so the
          operator can see the scope and clear it back to global. */}
      {/* Agent-scoped chip. Surfaces when opened from an agent's Audit tab
          (?agentId=…). getPolicyAuditTrail filters server-side to this
          agent's governed events — the same policy_audit_events the agent's
          integrity widget verifies — so "Open Audit trail" lands on real
          data instead of an empty envelope dashboard. */}
      {agentIdFilter && (
        <div className="rounded-v2-md border border-v2-action-border bg-v2-action-soft px-3 py-2 mb-4 flex items-center gap-2 text-[12px]">
          <Lock size={12} className="text-v2-action-strong" />
          <span className="text-v2-ink">Scoped to agent</span>
          <code className="font-mono-v2 text-[11px] text-v2-ink-strong">{agentIdFilter}</code>
          <button onClick={() => setAgentIdFilter('')}
                  className="ml-auto text-[11.5px] text-v2-action-strong hover:text-v2-ink underline">
            Clear scope
          </button>
        </div>
      )}

      {workflowIdFilter && (
        <div className="rounded-v2-md border border-v2-action-border bg-v2-action-soft px-3 py-2 mb-4 flex items-center gap-2 text-[12px]">
          <Lock size={12} className="text-v2-action-strong" />
          <span className="text-v2-ink">Scoped to workflow</span>
          <code className="font-mono-v2 text-[11px] text-v2-ink-strong">{workflowIdFilter}</code>
          <button onClick={() => setWorkflowIdFilter('')}
                  className="ml-auto text-[11.5px] text-v2-action-strong hover:text-v2-ink underline">
            Clear scope
          </button>
        </div>
      )}

      {/* RC-40 — Run-scoped chip. Surfaces when the page was opened via
          a Slowest-runs / RunsSubTab deep-link (?runId=…). Reload
          fetches /api/v1/workflows/runs/{runId}/audit and merges the
          lifecycle events in, so the operator sees the executed /
          completed / failed entries for THIS run without having to
          search. */}
      {runIdFilter && (
        <div className="rounded-v2-md border border-v2-action-border bg-v2-action-soft px-3 py-2 mb-4 flex items-center gap-2 text-[12px]">
          <Lock size={12} className="text-v2-action-strong" />
          <span className="text-v2-ink">Scoped to run</span>
          <code className="font-mono-v2 text-[11px] text-v2-ink-strong">{runIdFilter}</code>
          <button onClick={() => setRunIdFilter('')}
                  className="ml-auto text-[11.5px] text-v2-action-strong hover:text-v2-ink underline">
            Clear scope
          </button>
        </div>
      )}

      {/* Integrity banner */}
      {integrity && (() => {
        /* Three states, not two. A chain can be invalid because rows were altered, or
         * because they were appended out of order — both invalid, only one a security
         * event. Rendering them identically is what sent a race condition to an operator
         * as "BREAK DETECTED — investigate immediately" and cost hours of forensic work
         * on evidence that turned out to be intact. */
        const verdict = verdictPresentation(integrity, agentIdFilter);
        const border = verdict.tone === 'success' ? 'border-v2-success bg-v2-success-soft'
          : verdict.tone === 'warning' ? 'border-v2-warning bg-v2-warning-soft'
          : 'border-v2-danger bg-v2-danger-soft';
        const icon = verdict.tone === 'success'
          ? <CheckCircle size={20} className="text-v2-success" />
          : <AlertTriangle size={20} className={verdict.tone === 'warning' ? 'text-v2-warning' : 'text-v2-danger'} />;
        const detail = integrity.intact
          ? `${integrity.verified || integrity.events || 0} events cryptographically verified`
          : integrity.contentUnaltered
            ? `${integrity.breakCount || 0} ordering break(s) across ${integrity.events || 0} events — every record's own hash verifies, so nothing was altered. Open forensic review for the affected events.`
            : `${integrity.breakCount || 0} break(s) — at least one record does not match its own contents. Open forensic review before repairing anything.`;
        return (
        <div className={'rounded-v2-md border-l-4 px-4 py-3 mb-5 flex items-center gap-3 ' + border}>
          {icon}
          <div className="flex-1 text-[12.5px]">
            <div className="font-bold text-v2-ink">{verdict.title}</div>
            <div className="text-v2-ink-secondary">{detail}</div>
          </div>
          {!integrity.intact && (
            repairConfirm ? (
              /* Inline confirm — platform-native danger+ghost, NOT a browser
                 window.confirm (no-browser-confirm convention). */
              <div className="flex items-center gap-2">
                <span className="text-[11.5px] text-v2-ink-secondary max-w-[280px]">
                  Rewrites the chain hashes for this window to be self-consistent. Mutates audit data, logged at WARN, cannot be undone.
                </span>
                <Button variant="danger" size="sm" onClick={handleRepairChain} disabled={repairRunning}>
                  {repairRunning ? 'Repairing…' : 'Repair chain'}
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setRepairConfirm(false)} disabled={repairRunning}>
                  Cancel
                </Button>
              </div>
            ) : (
            <>
              <Button variant="danger" size="sm" onClick={() => setForensicOpen(true)}>
                Open forensic review
              </Button>
              {/* G345 — Self-service remediation. Available alongside
                  forensic review because most "broken" verdicts on
                  freshly-upgraded systems come from the pre-fix
                  timestamp-precision bug, not actual tampering. The
                  repair endpoint is idempotent: if breaks are real
                  tampering it'll surface the same breaks again on
                  the post-repair verify; if breaks were precision
                  artifacts they'll clear. */}
              {/* No repair button when any record's own hash disagrees with its contents.
                  A rebuild recomputes hashes from whatever the rows now say, so running
                  one over an altered record makes the alteration permanent and
                  self-consistent — it destroys the very evidence the break preserved. */}
              {verdict.canRepair && (
                <Button
                  variant="secondary"
                  size="sm"
                  iconLeft={<RefreshCw size={13} className={repairRunning ? 'animate-spin' : ''} />}
                  onClick={() => setRepairConfirm(true)}
                  disabled={repairRunning}
                  title="Re-link the chain for this window. Offered only where every record's own hash verifies, so no evidence can be overwritten. Idempotent."
                >
                  {repairRunning ? 'Repairing…' : 'Repair chain'}
                </Button>
              )}
            </>
            )
          )}
        </div>
        );
      })()}

      <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 mb-5">
        {/* When the window totals could not be fetched these are counts over the loaded
            page, which is a different number answering a different question. The label
            says which one you are reading rather than presenting either as the other. */}
        <MetricCard label={kpis.fromWindow ? 'Total events' : 'Events (loaded page)'}
                    value={kpis.total.toLocaleString()} tone="cyan" />
        <MetricCard label={kpis.fromWindow ? 'Allowed' : 'Allowed (loaded page)'}
                    value={kpis.allows.toLocaleString()} tone="success" />
        <MetricCard label={kpis.fromWindow ? 'Blocked' : 'Blocked (loaded page)'}
                    value={kpis.blocks.toLocaleString()} tone={kpis.blocks > 0 ? 'warning' : 'success'} />
        <MetricCard label={kpis.fromWindow ? 'Errors' : 'Errors (loaded page)'}
                    value={kpis.errors.toLocaleString()} tone={kpis.errors > 0 ? 'danger' : 'success'} />
        {/* G334 — Chain integrity KPI. Live snapshot from the last
            verify run; reads "—" until verify is invoked. Operators
            scanning the page get an immediate signal that something
            needs attention before drilling into the banner / modal. */}
        <MetricCard
          label={integrity == null ? 'Chain integrity' : chainLabelOf(integrity, agentIdFilter)}
          value={integrity == null ? '—' : integrity.intact ? 'Intact' : `${integrity.breakCount} break${integrity.breakCount === 1 ? '' : 's'}`}
          /* Out-of-order with every record's own hash verifying is not the same alarm as
             a record that disagrees with its own contents. */
          tone={integrity == null ? 'muted'
            : integrity.intact ? 'success'
            : integrity.contentUnaltered ? 'warning' : 'danger'}
        />
      </div>

      {/* RC-228 — Quick-filter presets. Always visible (no conditional
          on activeFilterChips) so operators can discover them. Today
          there's one preset (Forms); the strip is a host for future
          presets without re-laying-out the page. */}
      <div className="bg-v2-surface border border-v2-border-subtle rounded-v2-md shadow-v2-sm px-4 py-2 mb-3 flex items-center gap-2 flex-wrap">
        <span className="text-[11px] uppercase tracking-wider text-v2-ink-muted font-semibold">Presets</span>
        <button
          type="button"
          onClick={formsPresetActive ? clearFacets : applyFormsPreset}
          aria-pressed={formsPresetActive}
          className={
            'v2-focus-ring inline-flex items-center gap-1 text-[11.5px] font-medium rounded-v2-pill px-2.5 py-0.5 ' +
            'transition-colors duration-v2 ease-v2 ' +
            (formsPresetActive
              ? 'bg-v2-action text-white hover:bg-v2-action-strong'
              : 'bg-v2-action-soft text-v2-action-strong hover:bg-v2-action hover:text-white')
          }
          title="Filter to FORM_TASK_* and EXTERNAL_FORM_* event types"
        >
          {formsPresetActive ? 'Forms ×' : 'Forms'}
        </button>
      </div>

      {activeFilterChips.length > 0 && (
        <div className="bg-v2-surface border border-v2-border-subtle rounded-v2-md shadow-v2-sm px-4 py-2.5 mb-4 flex items-center gap-2 flex-wrap">
          <span className="text-[11px] uppercase tracking-wider text-v2-ink-muted font-semibold">Filters</span>
          {activeFilterChips.map((c) => (
            <button key={`${c.groupKey}:${c.optionKey}`} onClick={() => toggleFacet(c.groupKey, c.optionKey, false)}
              className="inline-flex items-center gap-1 text-[11.5px] font-medium bg-v2-action-soft text-v2-action-strong rounded-v2-pill px-2.5 py-0.5 hover:bg-v2-action hover:text-white">
              {c.label} ×
            </button>
          ))}
          <button onClick={clearFacets} className="ml-auto text-[11.5px] text-v2-ink-muted hover:text-v2-ink">Clear all</button>
        </div>
      )}

      {/* P7J-RUN-034 — operator-reported UI bug: the EVENT TYPE filter
        * column was a hard-coded 240px, narrower than one KPI tile in the
        * 5-column strip above (~287px at max-w-1500), so the filter card's
        * right edge didn't line up with the right edge of the first KPI
        * tile. Use the same per-tile width math here — `(100% - 4·gap-4) / 5`
        * — so the FacetRail visually sits underneath the "Total events" tile.
        * gap-5 on this grid keeps the same vertical breathing room. */}
      <div className="grid gap-5" style={{ gridTemplateColumns: 'calc((100% - 64px) / 5) 1fr' }}>
        <FacetRail groups={facetGroupsWithCounts} onToggle={toggleFacet} ariaLabel="Audit filters" />

        {filtered.length === 0 && !loading ? (
          <EmptyState
            icon={<Activity size={20} />}
            title={events.length === 0 ? 'No audit events in this window' : 'No events match filters'}
            description={events.length === 0
              ? 'Try a wider time window or trigger some agent executions to populate the audit trail.'
              : 'Adjust filters or clear them to see more.'}
            primaryAction={events.length === 0 ? null : <Button variant="primary" onClick={clearFacets}>Clear filters</Button>}
          />
        ) : (
          <Table
            ariaLabel="Audit events"
            density={density}
            columns={[
              { key: 'time', header: 'Time', render: (e) => (
                <span className="font-mono-v2 text-[10.5px] text-v2-ink-muted">{fmtTime(e.timestamp || e.createdAt)}</span>
              )},
              { key: 'type', header: 'Type', render: (e) => (
                <Pill tone="muted" size="sm">{(e.eventType || e.type || 'unknown').replace(/_/g, ' ').toLowerCase()}</Pill>
              )},
              { key: 'actor', header: 'Actor', render: (e) => {
                /* RC-23 / UI_DISPLAY_CONVENTIONS R1+R2 — Actor cell renders
                 * the entity name with a short-id suffix for forensic
                 * uniqueness. Names are stamped at write time in
                 * PolicyAuditService.persistEvent (V244); pre-V244 rows
                 * fall back to a graceful "user (shortid)" label per R3
                 * so historical rows render predictably without joining
                 * live tables. The pill keeps the visual category cue. */
                const shortId = (id) => id ? `${String(id).slice(0, 8)}…` : '';
                if (e.userId) {
                  const name = (e.userName && e.userName.trim()) ? e.userName : 'user';
                  return (
                    <span className="text-[12px]" title={e.userId}>
                      <Pill tone="cyan" size="sm">user</Pill>{' '}
                      <span className="text-v2-ink ml-1">{name}</span>{' '}
                      <code className="font-mono-v2 text-[10.5px] text-v2-ink-muted">({shortId(e.userId)})</code>
                    </span>
                  );
                }
                if (e.agentId) {
                  const name = (e.agentName && e.agentName.trim()) ? e.agentName : 'agent';
                  return (
                    <span className="text-[12px]" title={e.agentId}>
                      <Pill tone="action" size="sm">agent</Pill>{' '}
                      <span className="text-v2-ink ml-1">{name}</span>{' '}
                      <code className="font-mono-v2 text-[10.5px] text-v2-ink-muted">({shortId(e.agentId)})</code>
                    </span>
                  );
                }
                return <span className="text-[12px] text-v2-ink-muted">system</span>;
              }},
              { key: 'resource', header: 'Resource', render: (e) => {
                /* RC-23 — Resource cell: prefer stamped resource name +
                 * short id; fall back to raw resource string or em-dash. */
                const shortId = (id) => id ? `${String(id).slice(0, 8)}…` : '';
                const name = e.resourceName || e.skillName || e.workflowName;
                const id   = e.skillId || e.workflowId || e.resourceId;
                if (name && id) {
                  return (
                    <span className="text-[11.5px] text-v2-ink-secondary" title={id}>
                      <span className="text-v2-ink">{name}</span>{' '}
                      <code className="font-mono-v2 text-[10.5px] text-v2-ink-muted">({shortId(id)})</code>
                    </span>
                  );
                }
                if (name) return <span className="text-[11.5px] text-v2-ink-secondary">{name}</span>;
                if (id)   return <span className="text-[11.5px] text-v2-ink-secondary"><code className="font-mono-v2 text-[10.5px]">{shortId(id)}</code></span>;
                return <span className="text-[11.5px] text-v2-ink-muted">{e.resource || '—'}</span>;
              }},
              { key: 'outcome', header: 'Outcome', render: (e) => {
                /* G222 — backend stores uppercase decision; tone map keyed on
                 * uppercase too, but render the user-facing label lowercased. */
                const o = outcomeOf(e) || 'LOGGED';
                return <Pill tone={OUTCOME_TONE[o] || 'muted'} size="sm" dot>{o.toLowerCase()}</Pill>;
              }},
              /* G334 — Source badge column (NATIVE / EXTERNAL). V1's
                 WorkflowAuditPage made this visible at a glance; on
                 a mixed-source fleet the operator needs to know which
                 side of the trust boundary fired the event without
                 drilling into the slideout. */
              { key: 'source', header: 'Source', render: (e) => {
                const s = sourceOf(e);
                return s === 'EXTERNAL'
                  ? <Pill tone="warning" size="sm">External</Pill>
                  : s === 'NATIVE'
                  ? <Pill tone="action" size="sm">Native</Pill>
                  : <span className="text-[11.5px] text-v2-ink-muted">—</span>;
              }},
              { key: 'hash', header: 'Hash', align: 'right', render: (e) => (
                <code className="font-mono-v2 text-[10.5px] text-v2-ink-muted">{(e.eventHash || e.hash || e.previousEventHash || '').slice(0, 8)}</code>
              )},
              /* G334 — Per-row integrity status dot. Mirrors V1's
                 WorkflowEnforcementAuditPanel.HashChainStrip behaviour
                 but at the event-row level. Three states:
                 • broken — eventId is in the last verify's chainBreaks
                 • verified — last verify ran successfully and this event
                   wasn't flagged
                 • unverified — verify hasn't been run this session
                 Tooltip explains; tone communicates urgency. */
              { key: 'integrity', header: 'Chain', align: 'right', render: (e) => {
                const id = e.id || e.eventId;
                if (brokenEventIds.has(id)) {
                  return <span title="This event is part of a hash-chain break. Open forensic review."
                    className="inline-flex items-center gap-1 text-[10.5px] font-semibold text-v2-danger-strong">
                    <span className="w-2 h-2 rounded-full bg-v2-danger" /> broken
                  </span>;
                }
                if (integrity?.intact) {
                  return <span title="Verified intact in last chain verification" aria-label="Verified intact"
                    className="inline-flex items-center justify-end">
                    <span className="w-2 h-2 rounded-full bg-v2-success" />
                  </span>;
                }
                return <span title="Run Verify integrity to confirm this event's chain link" aria-label="Unverified"
                  className="inline-flex items-center justify-end">
                  <span className="w-2 h-2 rounded-full bg-v2-border-strong" />
                </span>;
              }},
            ]}
            rows={visibleRows}
            rowKey={(e) => e.id || e.eventId}
            onRowClick={openSlideout}
          />
        )}
      </div>

      {filtered.length > 0 && (
        <div className="grid gap-5 mt-3 grid-cols-1 lg:grid-cols-[240px_1fr]">
          <div />
          <Pagination
            totalItems={filtered.length}
            page={pg.page}
            pageSize={pg.pageSize}
            onPageChange={pg.setPage}
            onPageSizeChange={pg.setPageSize}
            noun="audit events"
          />
        </div>
      )}

      <Slideout
        isOpen={!!selected}
        onClose={closeSlideout}
        ariaLabel={selected ? `Audit event ${selected.id || selected.eventId} detail` : 'Event detail'}
        width={520}
        tabs={[
          { id: 'detail',   label: 'Detail' },
          { id: 'forensics', label: 'Five-Layer trace' },
          { id: 'related', label: 'Related events' },
          { id: 'chain',   label: 'Hash chain' },
        ]}
        activeTabId={slideoutTab}
        onTabChange={setSlideoutTab}
        header={selected && (
          <div>
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              {/* G222 — Use uppercase outcome accessor (backend decision is UPPER). */}
              {(() => { const o = outcomeOf(selected) || 'LOGGED'; return (
                <Pill tone={OUTCOME_TONE[o] || 'muted'} size="sm" dot>{o.toLowerCase()}</Pill>
              ); })()}
              <Pill tone="muted" size="sm">{(eventTypeOf(selected) || 'EVENT').replace(/_/g, ' ').toLowerCase()}</Pill>
              <Pill tone="trust" size="sm">
                <Lock size={10} className="inline mr-1" />
                signed
              </Pill>
              {/* G334 — Source pill in the slideout for at-a-glance
                  classification when the operator opens an event. */}
              {(() => { const s = sourceOf(selected); return s === 'EXTERNAL'
                ? <Pill tone="warning" size="sm">External</Pill>
                : s === 'NATIVE'
                ? <Pill tone="action" size="sm">Native</Pill>
                : null; })()}
              {/* G334 — Broken-chain badge if this event is in the
                  current chainBreaks set. Makes the forensic context
                  visible even when the operator navigated here from
                  a regular row click. */}
              {brokenEventIds.has(selected.id || selected.eventId) && (
                <Pill tone="danger" size="sm">
                  <AlertTriangle size={10} className="inline mr-1" />
                  chain break
                </Pill>
              )}
            </div>
            <h2 className="text-[16px] font-bold tracking-tight m-0">{selected.summary || selected.action || selected.description || selected.skillName || 'Event'}</h2>
            <div className="flex items-center gap-2 mt-0.5">
              <p className="text-[12px] text-v2-ink-secondary font-mono-v2 m-0">{fmtTime(selected.timestamp || selected.createdAt)}</p>
              {/* G334 — Copy event ID. Investigators routinely cite the
                  event ID in incident tickets / Slack threads; this
                  removes the manual select-and-copy step. */}
              {(selected.id || selected.eventId) && (
                <button
                  type="button"
                  className="text-[11px] text-v2-ink-muted hover:text-v2-ink underline decoration-dotted"
                  onClick={() => {
                    const id = selected.id || selected.eventId;
                    try {
                      navigator.clipboard?.writeText(String(id));
                      addToast({ tone: 'success', title: 'Event ID copied', description: String(id) });
                    } catch (_) {
                      /* Fallback — modern browsers should never hit this. */
                      addToast({ tone: 'warning', title: 'Copy failed', description: 'Clipboard API unavailable.' });
                    }
                  }}
                >
                  copy id
                </button>
              )}
            </div>
          </div>
        )}
      >
        {selected && slideoutTab === 'detail' && (
          <Card padding="sm">
            <Card.Header title="Event detail" />
            <Card.Body>
              <pre className="text-[10.5px] font-mono-v2 text-v2-ink-secondary bg-v2-muted rounded-v2-sm p-3 max-h-[400px] overflow-auto">
                {JSON.stringify(selected, null, 2)}
              </pre>
            </Card.Body>
          </Card>
        )}
        {selected && slideoutTab === 'forensics' && (
          <Card padding="sm">
            <Card.Header
              title="Five-Layer trace"
              subtitle="Which of the five enforcement layers fired for this event and what each verdict was."
            />
            <Card.Body>
              {(() => {
                /* G344 — The backend doesn't yet emit a single
                   `layerVerdicts` array per event (it records each
                   layer as its own discrete audit event). Until that
                   ships, synthesise a trace from the event's own
                   eventType + decision: light up the layer that
                   emitted this event, mark Audit (L5) as logged
                   (every event hits the hash chain), and leave the
                   rest as 'skipped' rather than misleading.

                   When the backend ever exposes `layerVerdicts`
                   inline — either at the top level or inside the
                   `details` JSONB — we use it as-is. */
                const fromTop  = Array.isArray(selected.layerVerdicts) ? selected.layerVerdicts : null;
                const fromDeep = Array.isArray(selected.details?.layerVerdicts) ? selected.details.layerVerdicts : null;
                const explicit = fromTop || fromDeep;
                if (explicit && explicit.length > 0) {
                  return <FiveLayerTrace layers={explicit} />;
                }
                /* Synthesis path. */
                const evType = eventTypeOf(selected);
                const decision = outcomeOf(selected);
                const EVENT_TO_LAYER = {
                  POLICY_RULE_EVALUATION: 1,
                  SEMANTIC_EVALUATION:    1,
                  DATA_CLASSIFICATION:    2,
                  DATA_REDACTION:         2,
                  TOKEN_CREATED:          2,
                  TOKEN_RESOLVED:         2,
                  TOKEN_EXPIRED:          2,
                  ANOMALY_DETECTED:       3,
                  EMERGENCY_OVERRIDE:     3,
                  AGENT_ENFORCEMENT:      3,
                  INFERENCE_CALL:         4,
                  MCP_TOOL_INVOCATION:    5,
                };
                const DECISION_TO_VERDICT = {
                  ALLOWED:   'allow',
                  BLOCKED:   'block',
                  WARN:      'warn',
                  REDACTED:  'warn',
                  ESCALATED: 'warn',
                  LOGGED:    'logged',
                  FAILED:    'block',
                };
                const firingLayer = EVENT_TO_LAYER[evType] || null;
                const firingVerdict = DECISION_TO_VERDICT[decision] || 'logged';
                const layers = [1,2,3,4,5].map((n) => {
                  if (n === firingLayer) return { layer: n, verdict: firingVerdict };
                  if (n === 5)           return { layer: 5, verdict: 'logged' };  // every event hits audit
                  return { layer: n, verdict: 'skipped' };
                });
                return (
                  <>
                    <FiveLayerTrace layers={layers} />
                    {!firingLayer && (
                      <p className="text-[11.5px] text-v2-ink-muted mt-3 italic">
                        Event type <code className="font-mono-v2">{evType || 'unknown'}</code> does not map to a specific
                        enforcement layer. Showing audit-only trace.
                      </p>
                    )}
                    <p className="text-[11px] text-v2-ink-muted mt-3 leading-snug">
                      <strong>Note:</strong> the platform records each governance layer as a separate audit event.
                      To see all five verdicts for a single request, filter the audit log by
                      its <code className="font-mono-v2">requestId</code> or <code className="font-mono-v2">workflowId</code> —
                      the related Layer 1 → Layer 5 events will appear as a sequence sharing that correlation key.
                    </p>
                  </>
                );
              })()}

              {/* G344 — Surface the event's `details` JSONB beneath the
                  trace so the operator gets the actual layer-specific
                  context (rule name, redaction count, anomaly score,
                  etc.) without having to switch to the Detail tab. */}
              {selected.details && typeof selected.details === 'object' && Object.keys(selected.details).length > 0 && (
                <div className="mt-4">
                  <div className="text-[10.5px] font-semibold uppercase tracking-wider text-v2-ink-muted mb-1.5">
                    Decision context
                  </div>
                  <pre className="text-[10.5px] font-mono-v2 text-v2-ink-secondary bg-v2-muted rounded-v2-sm p-3 max-h-[240px] overflow-auto m-0">
                    {JSON.stringify(selected.details, null, 2)}
                  </pre>
                </div>
              )}
            </Card.Body>
          </Card>
        )}
        {selected && slideoutTab === 'related' && (
          <Card padding="sm">
            <Card.Header title="Related events" />
            <Card.Body>
              <p className="text-v2-ink-muted text-[12.5px]">Related event correlation is queued. For now, filter the audit log by actor or resource ID to find related events.</p>
            </Card.Body>
          </Card>
        )}
        {selected && slideoutTab === 'chain' && (() => {
          const evId = selected.id || selected.eventId;
          const verifyState = perEventVerify[evId];
          return (
          <Card padding="sm">
            <Card.Header
              title="Hash chain"
              subtitle="Verifies this single event's hash in isolation. Does not walk the tenant chain — fast and safe to run on a filtered view."
            />
            <Card.Body>
              <dl className="grid grid-cols-1 gap-2 text-[12px]">
                {/* G222 — Backend field name is `previousEventHash` (not `prevHash`). */}
                <div><dt className="text-v2-ink-muted text-[10.5px] uppercase tracking-wider font-semibold">This event hash</dt><dd className="text-v2-ink mt-0.5 font-mono-v2 break-all">{selected.eventHash || selected.hash || '—'}</dd></div>
                <div><dt className="text-v2-ink-muted text-[10.5px] uppercase tracking-wider font-semibold">Previous hash</dt><dd className="text-v2-ink mt-0.5 font-mono-v2 break-all">{selected.previousEventHash || selected.prevHash || selected.previousHash || '—'}</dd></div>
                <div><dt className="text-v2-ink-muted text-[10.5px] uppercase tracking-wider font-semibold">Chain index</dt><dd className="text-v2-ink mt-0.5 font-mono-v2">{selected.chainIndex || selected.position || '—'}</dd></div>
              </dl>

              {/* G345 — Two scopes of verification, made explicit so the
                  operator can't accidentally trigger a million-event chain
                  walk from a single-event view. */}
              <div className="mt-3 grid grid-cols-1 gap-2">
                <Button
                  variant="primary"
                  size="sm"
                  iconLeft={<ShieldCheck size={13} className={verifyState?.state === 'verifying' ? 'animate-spin' : ''} />}
                  onClick={() => verifyOneEvent(selected)}
                  disabled={verifyState?.state === 'verifying'}
                >
                  {verifyState?.state === 'verifying' ? 'Verifying…' : 'Verify this event'}
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  iconLeft={<ShieldCheck size={13} />}
                  onClick={handleVerifyIntegrity}
                  title="Walks the entire chain in this tenant + time window. Heavy — use the page-level Verify integrity button for large windows."
                >
                  Verify full chain
                </Button>
              </div>

              {/* Per-event verification result. */}
              {verifyState && verifyState.state !== 'verifying' && (
                <div className={'mt-3 rounded-v2-sm border px-3 py-2 text-[12px] ' +
                  (verifyState.state === 'valid'   ? 'border-v2-success-border bg-v2-success-soft' :
                   verifyState.state === 'broken'  ? 'border-v2-danger-border bg-v2-danger-soft' :
                                                     'border-v2-warning-border bg-v2-warning-soft')}>
                  <div className="font-semibold flex items-center gap-1.5 mb-1">
                    {verifyState.state === 'valid'
                      ? <><CheckCircle size={13} className="text-v2-success" /> Event hash verified</>
                      : verifyState.state === 'broken'
                      ? <><AlertTriangle size={13} className="text-v2-danger" /> Hash mismatch</>
                      : <><AlertTriangle size={13} className="text-v2-warning-strong" /> {verifyState.state === 'unknown' ? 'No stored hash' : 'Verification error'}</>}
                  </div>
                  {verifyState.message && (
                    <div className="text-v2-ink-secondary mb-1">{verifyState.message}</div>
                  )}
                  {(verifyState.computedHash || verifyState.storedHash) && (
                    <div className="text-[10.5px] font-mono-v2 text-v2-ink-muted space-y-0.5 mt-1">
                      {verifyState.computedHash && (
                        <div className="break-all"><span className="text-v2-ink-muted">computed:</span> {verifyState.computedHash}</div>
                      )}
                      {verifyState.storedHash && (
                        <div className="break-all"><span className="text-v2-ink-muted">stored:&nbsp;&nbsp;</span> {verifyState.storedHash}</div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </Card.Body>
          </Card>
          );
        })()}
      </Slideout>

      {/* G186 — Forensic review modal.
       *
       * Surfaces the chainBreaks array from verifyAuditIntegrity with full
       * operator-actionable detail per break: event id, position in chain,
       * recorded vs computed hash, prev hash, and surrounding timestamps.
       *
       * Production-grade behavior:
       *   - Re-verify button re-runs verifyAuditIntegrity inline so the
       *     operator can confirm the state hasn't changed before exporting.
       *   - Export JSON button downloads the full forensic payload for
       *     off-platform analysis (digital evidence preservation).
       *   - Per-break Open event button drills into the event detail
       *     slideout for context (actor, payload, layer verdicts).
       *
       * Note: there is intentionally no "Mark investigated" / "Dismiss break"
       * action here — chain breaks are immutable cryptographic facts; the
       * only correct response is to investigate and restore the source of
       * truth, not to silence the alarm. */}
      <Modal
        isOpen={forensicOpen}
        onClose={() => setForensicOpen(false)}
        size="lg"
        title={
          <div className="flex items-center gap-2">
            <AlertTriangle size={16} className="text-v2-danger" />
            <span>Audit chain forensic review</span>
            {integrity?.breakCount > 0 && (
              <Pill tone="danger" size="sm">{integrity.breakCount} break{integrity.breakCount === 1 ? '' : 's'}</Pill>
            )}
          </div>
        }
        footer={
          <>
            <Button variant="ghost" onClick={() => setForensicOpen(false)}>Close</Button>
            <Button
              variant="secondary"
              iconLeft={<Download size={13} />}
              onClick={() => {
                /* Browser download — no server round-trip. The forensic
                 * payload is whatever we currently hold in state from the
                 * latest verify call. */
                const blob = new Blob(
                  [JSON.stringify({ exportedAt: new Date().toISOString(), timeWindow, integrity }, null, 2)],
                  { type: 'application/json' }
                );
                const a = document.createElement('a');
                a.href = URL.createObjectURL(blob);
                a.download = `audit-forensic-${new Date().toISOString().replace(/[:.]/g,'-')}.json`;
                a.click();
                setTimeout(() => URL.revokeObjectURL(a.href), 0);
                addToast({ tone: 'success', title: 'Forensic payload exported', description: 'JSON download initiated.' });
              }}
            >
              Export evidence
            </Button>
            <Button
              variant="primary"
              iconLeft={<ShieldCheck size={13} />}
              loading={forensicReVerifying}
              onClick={async () => {
                setForensicReVerifying(true);
                try { await handleVerifyIntegrity(); }
                finally { setForensicReVerifying(false); }
              }}
            >
              Re-verify chain
            </Button>
          </>
        }
      >
        {!integrity ? (
          <div className="text-[12.5px] text-v2-ink-muted">No integrity report loaded. Click "Verify chain integrity" first.</div>
        ) : integrity.intact ? (
          <div className="flex items-center gap-2 rounded-v2-sm border border-v2-success-border bg-v2-success-soft p-3 text-[12.5px]">
            <CheckCircle size={14} className="text-v2-success" />
            <div>
              <div className="font-semibold text-v2-ink">Chain intact</div>
              <div className="text-v2-ink-secondary">{integrity.verified} events cryptographically verified in this time window.</div>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="rounded-v2-sm border border-v2-danger-border bg-v2-danger-soft p-3 text-[12.5px]">
              <div className="font-semibold text-v2-danger flex items-center gap-1.5">
                <AlertTriangle size={13} /> Hash-chain integrity violation
              </div>
              <div className="text-v2-ink-secondary mt-1">
                {integrity.breakCount} break{integrity.breakCount === 1 ? '' : 's'} found across
                {' '}{integrity.events.toLocaleString()} events. Each break below shows the
                position in the chain where the recorded hash does not match the hash computed
                from the previous event's signature + this event's payload. Investigate
                immediately — chain breaks indicate either a tampered audit record or a
                non-conformant writer.
              </div>
            </div>

            {/* G334 — Bulk-break perf cap. A cascading break can run
                tens of thousands of entries (each downstream event
                reports its broken link). Rendering them all locks the
                modal. Show the first FORENSIC_PAGE_CAP entries plus
                a hint, with a Show-all toggle the operator can opt
                into when they truly need the full list in-DOM. The
                Export evidence button writes the full unfiltered
                payload regardless of this UI cap. */}
            {integrity.breakCount > FORENSIC_PAGE_CAP && !forensicShowAll && (
              <div className="rounded-v2-sm bg-v2-warning-soft border border-v2-warning-border px-3 py-2 text-[12px] flex items-center gap-3">
                <AlertTriangle size={13} className="text-v2-warning-strong shrink-0" />
                <div className="flex-1 text-v2-ink">
                  Showing first <strong>{FORENSIC_PAGE_CAP}</strong> of <strong>{integrity.breakCount.toLocaleString()}</strong> breaks.
                  Cascading breaks usually share a single root cause — investigate the earliest break first.
                  Use <em>Export evidence</em> for the full forensic payload.
                </div>
                <Button variant="ghost" size="xs" onClick={() => setForensicShowAll(true)}>Show all (slow)</Button>
              </div>
            )}

            {(forensicShowAll
              ? (integrity.chainBreaks || [])
              : (integrity.chainBreaks || []).slice(0, FORENSIC_PAGE_CAP)
            ).map((br, idx) => {
              /* G334 — Locate the broken event in the loaded set so
                 "Open event" can hand it to the slideout. If the
                 event is outside the current window we degrade
                 gracefully (button disabled, hint shown). */
              const brokenEvent = (events || []).find((e) => (e.id || e.eventId) === br.eventId);
              /* G334 — Two break kinds emitted by the backend require
                 different operator framing:
                   chain — previous-hash link mismatch (the chain is
                     stitched wrong; either the prior event was edited
                     or a write skipped recording the link)
                   hash  — the event's own recorded hash doesn't match
                     the value re-computed from its inputs (the event's
                     payload or hash field was tampered)
                 Both indicate tampering or non-conformant writers, but
                 the remediation diverges: chain breaks blame the prior
                 record; hash mismatches blame the broken record itself. */
              const kindLabel = br.kind === 'chain'
                ? 'Previous-hash mismatch'
                : br.kind === 'hash'
                ? 'Event-hash mismatch'
                : 'Chain anomaly';
              const kindTone = br.kind ? 'danger' : 'warning';
              /* G334 — Emphasise the first break as the likely root
                 cause. A hash-chain break cascades: events downstream
                 of the first tampered record will all show "previous
                 hash mismatch" because their link points to what the
                 prior event's pre-tamper hash was. Investigating Break
                 #1 typically explains every subsequent entry. */
              const isFirst = idx === 0;
              return (
              <div key={idx} className={
                'rounded-v2-sm p-3 space-y-1.5 ' +
                (isFirst
                  ? 'border-2 border-v2-danger bg-v2-danger-soft/40'
                  : 'border border-v2-border-subtle bg-v2-surface')
              }>
                <div className="flex items-center justify-between gap-2">
                  <div className="text-[12.5px] font-semibold text-v2-ink flex items-center gap-1.5 flex-wrap">
                    <Lock size={12} className="text-v2-danger" />
                    Break #{idx + 1}
                    {isFirst && integrity.breakCount > 1 && (
                      <Pill tone="danger" size="sm">root cause</Pill>
                    )}
                    <Pill tone={kindTone} size="sm">{kindLabel}</Pill>
                    {br.eventId && <span className="text-v2-ink-muted text-[11px] font-mono-v2">event {String(br.eventId).slice(0,8)}…</span>}
                  </div>
                  <div className="flex items-center gap-2">
                    {br.position != null && (
                      <span className="text-[11px] text-v2-ink-muted">chain position {br.position}</span>
                    )}
                    {/* G334 — Drill from forensic break → event slideout. */}
                    <Button
                      variant="secondary"
                      size="xs"
                      disabled={!brokenEvent}
                      title={brokenEvent ? 'Open this event in the slideout for context' : 'Event outside loaded window — widen time range and re-verify'}
                      onClick={() => {
                        if (brokenEvent) {
                          setForensicOpen(false);
                          openSlideout(brokenEvent);
                          setSlideoutTab('chain');
                        }
                      }}
                    >
                      <Eye size={11} className="inline mr-1" />
                      Open event
                    </Button>
                  </div>
                </div>
                {br.message && (
                  <div className="text-[11.5px] text-v2-ink-secondary">{br.message}</div>
                )}
                <div className="grid grid-cols-2 gap-2 text-[10.5px] font-mono-v2 text-v2-ink-secondary">
                  {br.expectedHash && (
                    <div>
                      <span className="text-v2-ink-muted">
                        {br.kind === 'chain' ? 'prev hash (expected):' : 'computed:'}
                      </span>{' '}
                      <span className="break-all">{br.expectedHash}</span>
                    </div>
                  )}
                  {br.actualHash && (
                    <div>
                      <span className="text-v2-ink-muted">
                        {br.kind === 'chain' ? 'prev hash (stored):' : 'stored:'}
                      </span>{' '}
                      <span className="break-all">{br.actualHash}</span>
                    </div>
                  )}
                  {br.previousHash && (
                    <div className="col-span-2"><span className="text-v2-ink-muted">prevHash:</span> <span className="break-all">{br.previousHash}</span></div>
                  )}
                  {br.timestamp && (
                    <div className="col-span-2"><span className="text-v2-ink-muted">at:</span> {fmtTime(br.timestamp)}</div>
                  )}
                </div>
                {/* G334 — Fallback display for unparsable break strings.
                    If the backend ever changes its format and the regex
                    doesn't match, the operator still sees the original
                    verbatim line instead of an empty card. */}
                {!br.kind && typeof br.message === 'string' && br.message && (
                  <div className="text-[10.5px] font-mono-v2 text-v2-ink-muted bg-v2-muted rounded-v2-sm p-2 break-all">{br.message}</div>
                )}
              </div>
            );
            })}
          </div>
        )}
      </Modal>
    </div>
  );
}

export default function ResolvedAuditTrailPage(props) {
  const url = typeof window !== 'undefined' ? new URL(window.location.href) : null;
  const legacy = url?.searchParams?.get('legacy') === '1';
  if (legacy) return <WorkflowAuditPage {...props} />;
  return <AuditTrailPageV2Inner {...props} />;
}

export { AuditTrailPageV2Inner };
